/*
  Shared Singles Grid Engine

  Purpose
  -------
  This file owns behavior that is common to every singles game:

  - collection JSON loading and caching
  - grouping condition variants into one card
  - availability filtering
  - card-name searching
  - configuration-driven facet filters
  - sorting
  - grid/list switching
  - condition selection
  - saved view, availability, and sort preferences
  - filter option generation
  - loading, empty, and error states

  Game files remain responsible for:

  - game-specific metadata composition
  - game-specific filter definitions
  - game-specific icons and visual assets
  - game-specific finish vocabularies and styling
  - genuinely game-specific renderer hooks

  Expected shared hooks in the Liquid markup
  ------------------------------------------

  [data-singles-collection]
  [data-singles-grid]
  [data-singles-grid-loading]
  [data-singles-stock-filter]
  [data-singles-view]
  [data-singles-sort]
  [data-singles-name-filter]
  [data-singles-name-clear]
  [data-singles-clear-filters]
  [data-singles-filter-options="filter-key"]

  Expected shared hooks in rendered card HTML
  -------------------------------------------

  [data-singles-card]
  [data-card-key="product-handle"]
  [data-singles-condition]
  [data-variant-id="variant-id"]
*/

console.log("Shared Singles Grid JS loaded");

(function (window, document) {
  "use strict";

  if (window.SinglesGrid && window.SinglesGrid.version) {
    return;
  }

  var VERSION = "1.1.0";

  var DEFAULT_PAGE_SIZE = 50;
  var DEFAULT_MAXIMUM_PAGES = 5000;
  var DEFAULT_SEARCH_DELAY = 120;
  var DEFAULT_EAGER_IMAGE_COUNT = 8;

  var DEFAULT_MAX_CONCURRENT_REQUESTS = 5;
  var DEFAULT_REQUEST_RETRIES = 2;
  var DEFAULT_RETRY_DELAY = 400;
  var DEFAULT_REQUEST_TIMEOUT = 15000;

  var collectionDataCache = {};
  var collectionLoadingPromises = {};
  var collectionShellCache = {};
  var collectionShellLoadingPromises = {};

  /* ======================================================
     General helpers
  ====================================================== */

  function normalizeText(value) {
    return String(value == null ? "" : value).trim();
  }

  function normalizeLower(value) {
    return normalizeText(value).toLowerCase();
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function escapeAttribute(value) {
    return escapeHtml(value);
  }

  function isAvailable(row) {
    return Boolean(
      row &&
      (
        row.available === true ||
        row.available === "true" ||
        row.available === 1 ||
        row.available === "1"
      )
    );
  }

  function getQuantity(row) {
    var quantity = Number(row && row.quantity);

    if (!Number.isFinite(quantity)) {
      return isAvailable(row) ? 1 : 0;
    }

    return quantity;
  }

  function parsePrice(value) {
    if (value === null || value === undefined || value === "") {
      return Number.NaN;
    }

    if (typeof value === "number") {
      return Number.isFinite(value) ? value : Number.NaN;
    }

    var cleaned = String(value)
      .replace(/[$,]/g, "")
      .trim();

    if (!cleaned) {
      return Number.NaN;
    }

    var parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }

  function getPriceDisplay(variant) {
    if (!variant) {
        return "";
    }

    return normalizeText(
        variant.price_display
    );
  }

  function compareText(a, b) {
    return normalizeText(a).localeCompare(
      normalizeText(b),
      undefined,
      {
        numeric: true,
        sensitivity: "base"
      }
    );
  }

  function comparePrices(a, b, descending) {
    var priceA = parsePrice(a);
    var priceB = parsePrice(b);

    if (!Number.isFinite(priceA) && !Number.isFinite(priceB)) {
      return 0;
    }

    /* Keep missing prices at the end in both directions. */
    if (!Number.isFinite(priceA)) {
      return 1;
    }

    if (!Number.isFinite(priceB)) {
      return -1;
    }

    return descending
      ? priceB - priceA
      : priceA - priceB;
  }

  function normalizeCardSearchText(value) {
    return normalizeLower(value)
      .replace(/[\u2019']/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function stripTitleDetails(value) {
    return normalizeText(value).replace(/\s*\([^)]*\)\s*$/, "").trim();
  }

  function shortCondition(condition) {
    var value = normalizeLower(condition);

    if (value === "near mint" || value === "near-mint" || value === "nm") {
      return "NM";
    }

    if (
      value === "light play" ||
      value === "lightly played" ||
      value === "light-played" ||
      value === "lp"
    ) {
      return "LP";
    }

    if (
      value === "play" ||
      value === "played" ||
      value === "pl"
    ) {
      return "PL";
    }

    if (
      value === "moderate play" ||
      value === "moderately played" ||
      value === "moderate-played" ||
      value === "mp"
    ) {
      return "MP";
    }

    if (
      value === "heavy play" ||
      value === "heavily played" ||
      value === "heavy-played" ||
      value === "hp"
    ) {
      return "HP";
    }

    if (value === "damaged" || value === "dmg") {
      return "DMG";
    }

    return normalizeText(condition);
  }

  function conditionRank(condition) {
    var order = {
      NM: 0,
      LP: 1,
      PL: 2,
      MP: 3,
      HP: 4,
      DMG: 5
    };

    var shortName = shortCondition(condition);

    return Object.prototype.hasOwnProperty.call(order, shortName)
      ? order[shortName]
      : 99;
  }

  function toArray(value) {
    if (Array.isArray(value)) {
      return value.slice();
    }

    if (value === null || value === undefined || value === "") {
      return [];
    }

    return [value];
  }

  function uniqueValues(values) {
    var seen = {};
    var output = [];

    values.forEach(function (value) {
      var displayValue = normalizeText(value);
      var key = displayValue.toLowerCase();

      if (!displayValue || seen[key]) {
        return;
      }

      seen[key] = true;
      output.push(displayValue);
    });

    return output;
  }

  function safeGetStoredValue(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeSetStoredValue(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* Current-page behavior still works without localStorage. */
    }
  }

  function dispatch(name, detail) {
    document.dispatchEvent(
      new CustomEvent(name, {
        detail: detail || {}
      })
    );
  }


  function refreshThemeWishlist() {
    if (
        window.T4SThemeSP &&
        window.T4SThemeSP.Wishlist &&
        typeof window.T4SThemeSP.Wishlist.updateAll ===
        "function"
    ) {
        window.T4SThemeSP.Wishlist.updateAll();
    }

    document.dispatchEvent(
        new Event(
        "theme:wishlist:loaded"
        )
    );

    document.dispatchEvent(
        new Event(
        "t4s:wishlist:loaded"
        )
    );
  }

  /* ======================================================
    Theme integrations
  ====================================================== */

  function initializeKallesButtons(
    container
  ) {
    var $t4s =
      window.jQuery_T4NT ||
      window.jQuery;

    if ($t4s && container) {
      var event =
        $t4s.Event(
          "submitAtc:t4s"
        );

      event.$container =
        $t4s(container);

      $t4s(document).trigger(
        event
      );
    }

    if (
      window.T4SThemeSP &&
      typeof window.T4SThemeSP.PopupMFP ===
        "function"
    ) {
      window.T4SThemeSP.PopupMFP();
    }
  }

  function initializeThemeWishlist() {
    if (
      window.T4SThemeSP &&
      typeof window.T4SThemeSP.initWishlist ===
        "function"
    ) {
      window.T4SThemeSP.initWishlist();
    }
  }

  var notifyEventsBound = false;
  var pendingNotifyVariantId = "";

  function bindNotifyButtons() {
    if (notifyEventsBound) {
      return;
    }

    notifyEventsBound = true;

    document.addEventListener(
      "click",
      function(event) {
        var button =
          event.target.closest(
            ".singles-notify-button" +
            "[data-klaviyo-form-id]"
          );

        if (!button) {
          return;
        }

        event.preventDefault();

        /*
          Disabled Notify buttons intentionally
          remain inactive until the feature is
          re-enabled.
        */
        if (
          button.disabled ||
          button.getAttribute(
            "aria-disabled"
          ) === "true"
        ) {
          return;
        }

        var formId =
          normalizeText(
            button.getAttribute(
              "data-klaviyo-form-id"
            )
          );

        if (!formId) {
          return;
        }

        pendingNotifyVariantId =
          normalizeText(
            button.getAttribute(
              "data-variant-id"
            )
          );

        if (!pendingNotifyVariantId) {
          console.error(
            "Notify button is missing a Shopify variant ID."
          );
          return;
        }

        var variantId =
          normalizeText(
            button.getAttribute(
              "data-variant-id"
            )
          );

        var productHandle =
          normalizeText(
            button.getAttribute(
              "data-product-handle"
            )
          );

        if (!variantId || !productHandle) {
          console.error(
            "Singles Notify is missing a product handle or variant ID."
          );
          return;
        }

        /*
          Remember where the customer came from.
        */
        try {
          window.sessionStorage.setItem(
            "singlesNotifyReturnUrl",
            window.location.href
          );
        } catch (error) {
          /* Return button can fall back to browser history. */
        }

        var productUrl =
          new URL(
            "/products/" +
              encodeURIComponent(productHandle),
            window.location.origin
          );

        productUrl.searchParams.set(
          "variant",
          variantId
        );

        productUrl.searchParams.set(
          "singles_notify",
          "1"
        );

        productUrl.searchParams.set(
          "singles_notify_variant",
          variantId
        );

        productUrl.searchParams.set(
          "singles_notify_form",
          formId
        );

        window.location.assign(
          productUrl.toString()
        );
      }
    );
  }

  function subscribeToBackInStock(
    email,
    companyId,
    variantId
  ) {
    var payload = {
      data: {
        type: "back-in-stock-subscription",

        attributes: {
          profile: {
            data: {
              type: "profile",

              attributes: {
                email: email
              }
            }
          },

          channels: [
            "EMAIL"
          ]
        },

        relationships: {
          variant: {
            data: {
              type: "catalog-variant",

              id:
                "$shopify:::$default:::" +
                variantId
            }
          }
        }
      }
    };

    return window.fetch(
      "https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=" +
        encodeURIComponent(companyId),
      {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
          "revision": "2026-07-15"
        },

        body: JSON.stringify(payload)
      }
    )
      .then(function(response) {
        if (response.status === 202) {
          console.log(
            "Back in stock subscription created:",
            variantId
          );

          return true;
        }

        return response
          .text()
          .then(function(body) {
            throw new Error(
              "Klaviyo Back in Stock failed (" +
                response.status +
                "): " +
                body
            );
          });
      });
  }

  function bindKlaviyoNotifyForm() {
    window.addEventListener(
      "klaviyoForms",
      function(event) {
        var detail =
          event && event.detail
            ? event.detail
            : {};

        if (
          detail.type !== "submit" ||
          detail.formId !== "W2nZC4"
        ) {
          return;
        }

        if (!pendingNotifyVariantId) {
          console.error(
            "No Shopify variant was selected for Back in Stock."
          );
          return;
        }

        var metaData =
          detail.metaData || {};

        var email =
          normalizeText(
            metaData.email
          );

        var companyId =
          normalizeText(
            detail.companyId
          );

        if (!email) {
          console.error(
            "Klaviyo form submission did not include an email address."
          );
          return;
        }

        if (!companyId) {
          console.error(
            "Klaviyo form submission did not include a company ID."
          );
          return;
        }

        subscribeToBackInStock(
          email,
          companyId,
          pendingNotifyVariantId
        )
          .then(function(success) {
            if (success) {
              pendingNotifyVariantId = "";
            }
          })
          .catch(function(error) {
            console.error(
              "Unable to create Back in Stock subscription:",
              error
            );
          });
      }
    );
  }
  /* ======================================================
     Collection loader and cache
  ====================================================== */

  function appendQueryParameter(url, key, value) {
    var parsed;

    try {
      parsed = new URL(url, window.location.origin);
      parsed.searchParams.set(key, String(value));
      return parsed.toString();
    } catch (error) {
      var separator = String(url).indexOf("?") === -1 ? "?" : "&";
      return String(url) + separator + encodeURIComponent(key) + "=" + encodeURIComponent(value);
    }
  }

  function createPageUrl(baseUrl, pageNumber, sortValue) {
    var pageUrl = appendQueryParameter(baseUrl, "page", pageNumber);

    if (sortValue) {
      pageUrl = appendQueryParameter(pageUrl, "sort_by", sortValue);
    }

    return pageUrl;
  }

  function parseSinglesRows(
    text,
    sourceLabel
  ) {
    var cleaned =
      normalizeText(text);

    if (!cleaned) {
      return [];
    }

    var parsed;

    try {
      parsed =
        JSON.parse(cleaned);

    } catch (error) {
      console.error(
        "Could not parse singles JSON:",
        sourceLabel,
        error
      );

      console.error(
        "Invalid response beginning:",
        cleaned.slice(0, 500)
      );

      console.error(
        "Invalid response ending:",
        cleaned.slice(-500)
      );

      var parseError =
        new Error(
          "The singles data was not valid JSON."
        );

      parseError.source =
        sourceLabel;

      throw parseError;
    }

    if (Array.isArray(parsed)) {
      return parsed;
    }

    if (
      parsed &&
      Array.isArray(parsed.rows)
    ) {
      return parsed.rows;
    }

    if (
      parsed &&
      Array.isArray(parsed.products)
    ) {
      return parsed.products;
    }

    console.warn(
      "Unexpected singles JSON structure:",
      sourceLabel,
      parsed
    );

    return [];
  }

  function waitForCollectionRetry(delay) {
    return new Promise(function(resolve) {
        window.setTimeout(
        resolve,
        Math.max(0, Number(delay) || 0)
        );
    });
    }


    function shouldRetryCollectionRequest(error) {
    /*
        Network errors and request timeouts normally have
        no HTTP status, so they should be retried.
    */
    if (
        !error ||
        error.status === undefined ||
        error.status === null
    ) {
        return true;
    }

    var status = Number(error.status);

    return (
        status === 408 ||
        status === 429 ||
        status >= 500
    );
    }


    function fetchCollectionPageOnce(
    pageUrl,
    pageNumber,
    options
    ) {
    options = options || {};

    var requestTimeout =
        options.requestTimeout !== undefined
        ? Number(options.requestTimeout)
        : DEFAULT_REQUEST_TIMEOUT;

    if (
        !Number.isFinite(requestTimeout) ||
        requestTimeout < 0
    ) {
        requestTimeout =
        DEFAULT_REQUEST_TIMEOUT;
    }

    var controller =
        typeof window.AbortController === "function"
        ? new window.AbortController()
        : null;

    var timeoutId = null;

    var requestOptions = {
        method: "GET",
        credentials: "same-origin",
        headers: {
        Accept: "text/html"
        }
    };

    if (controller) {
        requestOptions.signal =
        controller.signal;
    }

    if (
        controller &&
        requestTimeout > 0
    ) {
        timeoutId =
        window.setTimeout(
            function() {
            controller.abort();
            },
            requestTimeout
        );
    }

    return window.fetch(
        pageUrl,
        requestOptions
    )
        .then(function(response) {
        if (!response.ok) {
            var responseError =
            new Error(
                "Could not load page " +
                pageNumber +
                " of the singles collection. " +
                "Shopify returned HTTP " +
                response.status +
                "."
            );

            responseError.status =
            response.status;

            throw responseError;
        }

        return response.text();
        })
        .then(function(text) {
        return parseSinglesRows(
            text,
            pageUrl
        );
        })
        .catch(function(error) {
        if (
            controller &&
            controller.signal.aborted
        ) {
            var timeoutError =
            new Error(
                "Page " +
                pageNumber +
                " of the singles collection timed out."
            );

            timeoutError.code =
            "SINGLES_REQUEST_TIMEOUT";

            throw timeoutError;
        }

        throw error;
        })
        .finally(function() {
        if (timeoutId !== null) {
            window.clearTimeout(
            timeoutId
            );
        }
        });
    }


    function requestCollectionPage(
    baseUrl,
    pageNumber,
    options
    ) {
    options = options || {};

    var pageUrl =
        createPageUrl(
        baseUrl,
        pageNumber,
        options.serverSort || ""
        );

    var requestRetries =
        options.requestRetries !== undefined
        ? Number(options.requestRetries)
        : DEFAULT_REQUEST_RETRIES;

    if (
        !Number.isFinite(requestRetries) ||
        requestRetries < 0
    ) {
        requestRetries =
        DEFAULT_REQUEST_RETRIES;
    }

    requestRetries =
        Math.floor(requestRetries);

    var retryDelay =
        options.retryDelay !== undefined
        ? Number(options.retryDelay)
        : DEFAULT_RETRY_DELAY;

    if (
        !Number.isFinite(retryDelay) ||
        retryDelay < 0
    ) {
        retryDelay =
        DEFAULT_RETRY_DELAY;
    }

    var completedRetries = 0;

    function attemptRequest() {
        return fetchCollectionPageOnce(
        pageUrl,
        pageNumber,
        options
        )
        .catch(function(error) {
            if (
            completedRetries >= requestRetries ||
            !shouldRetryCollectionRequest(error)
            ) {
            throw error;
            }

            var currentRetry =
            completedRetries + 1;

            var currentDelay =
            retryDelay *
            Math.pow(
                2,
                completedRetries
            );

            completedRetries += 1;

            console.warn(
            "Retrying singles collection page " +
            pageNumber +
            " (" +
            currentRetry +
            "/" +
            requestRetries +
            ") in " +
            currentDelay +
            "ms.",
            error
            );

            return waitForCollectionRetry(
            currentDelay
            )
            .then(
                attemptRequest
            );
        });
    }

    return attemptRequest();
    }

  function getCollectionCacheKey(baseUrl, options) {
    options = options || {};

    return [
        normalizeText(baseUrl),
        normalizeText(options.serverSort),
        Number(options.productCount || 0),
        Number(
        options.pageSize ||
        DEFAULT_PAGE_SIZE
        ),
        Number(
        options.maximumPages ||
        DEFAULT_MAXIMUM_PAGES
        )
    ].join("::");
    }

  function loadCollectionSequentially(baseUrl, options) {
    var rows = [];
    var pageNumber = 1;
    var maximumPages = Number(options.maximumPages || DEFAULT_MAXIMUM_PAGES);

    function nextPage() {
      if (pageNumber > maximumPages) {
        console.warn("Singles grid reached its safety page limit.");
        return Promise.resolve(rows);
      }

      return requestCollectionPage(baseUrl, pageNumber, options)
        .then(function (pageRows) {
          if (!pageRows.length) {
            return rows;
          }

          rows = rows.concat(pageRows);
          pageNumber += 1;
          return nextPage();
        });
    }

    return nextPage();
  }

  function loadCollectionInParallel(
    baseUrl,
    options
    ) {
    options = options || {};

    var productCount =
        Number(
        options.productCount || 0
        );

    var pageSize =
        Number(
        options.pageSize ||
        DEFAULT_PAGE_SIZE
        );

    if (
        !Number.isFinite(pageSize) ||
        pageSize < 1
    ) {
        pageSize =
        DEFAULT_PAGE_SIZE;
    }

    var pageCount =
        Math.ceil(
        productCount / pageSize
        );

    if (!pageCount) {
        return Promise.resolve([]);
    }

    var maximumPages =
        Number(
        options.maximumPages ||
        DEFAULT_MAXIMUM_PAGES
        );

    if (
        !Number.isFinite(maximumPages) ||
        maximumPages < 1
    ) {
        maximumPages =
        DEFAULT_MAXIMUM_PAGES;
    }

    maximumPages =
        Math.floor(maximumPages);

    if (pageCount > maximumPages) {
        console.warn(
        "Singles collection page count was limited from " +
        pageCount +
        " to " +
        maximumPages +
        "."
        );

        pageCount =
        maximumPages;
    }

    var maxConcurrentRequests =
        options.maxConcurrentRequests !== undefined
        ? Number(
            options.maxConcurrentRequests
            )
        : DEFAULT_MAX_CONCURRENT_REQUESTS;

    if (
        !Number.isFinite(
        maxConcurrentRequests
        ) ||
        maxConcurrentRequests < 1
    ) {
        maxConcurrentRequests =
        DEFAULT_MAX_CONCURRENT_REQUESTS;
    }

    maxConcurrentRequests =
        Math.max(
        1,
        Math.min(
            pageCount,
            Math.floor(
            maxConcurrentRequests
            )
        )
        );

    /*
        Store each response in its original page position.
        Even though pages finish in different orders, the
        final rows remain page 1, page 2, page 3, etc.
    */
    var pageResults =
        new Array(pageCount);

    var nextPageNumber = 1;

    function runWorker() {
        if (
        nextPageNumber > pageCount
        ) {
        return Promise.resolve();
        }

        var currentPageNumber =
        nextPageNumber;

        nextPageNumber += 1;

        return requestCollectionPage(
        baseUrl,
        currentPageNumber,
        options
        )
        .then(function(rows) {
            pageResults[
            currentPageNumber - 1
            ] = Array.isArray(rows)
            ? rows
            : [];
        })
        .then(runWorker);
    }

    var workers = [];

    for (
        var workerIndex = 0;
        workerIndex <
        maxConcurrentRequests;
        workerIndex += 1
    ) {
        workers.push(
        runWorker()
        );
    }

    return Promise.all(workers)
        .then(function() {
        var rows = [];

        pageResults.forEach(
            function(pageRows) {
            if (
                Array.isArray(pageRows)
            ) {
                rows =
                rows.concat(pageRows);
            }
            }
        );

        return rows;
        });
    }

  function loadCompleteCollection(baseUrl, options) {
    options = options || {};

    var forceReload = options.forceReload === true;
    var cacheKey = getCollectionCacheKey(baseUrl, options);

    if (!forceReload && collectionDataCache[cacheKey]) {
      return Promise.resolve(collectionDataCache[cacheKey].slice());
    }

    if (!forceReload && collectionLoadingPromises[cacheKey]) {
      return collectionLoadingPromises[cacheKey];
    }

    var loader = Number(options.productCount || 0) > 0
      ? loadCollectionInParallel
      : loadCollectionSequentially;

    var loadingPromise = loader(baseUrl, options)
      .then(function (rows) {
        collectionDataCache[cacheKey] = rows.slice();
        return rows;
      })
      .finally(function () {
        delete collectionLoadingPromises[cacheKey];
      });

    collectionLoadingPromises[cacheKey] = loadingPromise;
    return loadingPromise;
  }

  function getCollectionHandleFromUrl(url) {
    try {
      var parsedUrl = new URL(url, window.location.origin);
      var match = parsedUrl.pathname.match(/^\/collections\/([^/]+)\/?$/);

      return match ? decodeURIComponent(match[1]) : "";
    } catch (error) {
      return "";
    }
  }

  function readCollectionRootData(root) {
    if (!root) {
        return {
        jsonUrl: "",
        productCount: 0,
        defaultSort: ""
        };
    }

    return {
      jsonUrl:
        normalizeText(
          root.getAttribute(
            "data-json-url"
          )
        ),

        jsonUrl:
        normalizeText(
            root.getAttribute(
            "data-json-url"
            )
        ),

        productCount:
        Number(
            root.getAttribute(
            "data-product-count"
            ) || 0
        ),

        defaultSort:
        normalizeText(
            root.getAttribute(
            "data-default-sort"
            )
        )
    };
    }


    function copyCollectionRootData(
    currentRoot,
    destinationRoot
    ) {
    if (
        !currentRoot ||
        !destinationRoot
    ) {
        return;
    }

    [
        "data-singles-game",
        "data-collection-handle",
        "data-json-url",
        "data-product-count",
        "data-default-sort"
    ].forEach(function(attributeName) {
        var value =
        destinationRoot.getAttribute(
            attributeName
        );

        if (value === null) {
        currentRoot.removeAttribute(
            attributeName
        );
        } else {
        currentRoot.setAttribute(
            attributeName,
            value
        );
        }
    });
    }


    function loadCollectionRoot(
    root,
    options
    ) {
    options = options || {};

    var rootData =
        readCollectionRootData(root);

    if (!rootData.jsonUrl) {
        return Promise.reject(
        new Error(
            "The collection JSON URL is missing."
        )
        );
    }

    var loaderOptions =
        Object.assign(
        {},
        options
        );

    if (
        loaderOptions.productCount ===
        undefined
    ) {
        loaderOptions.productCount =
        rootData.productCount;
    }

    if (
        loaderOptions.serverSort ===
        undefined
    ) {
        loaderOptions.serverSort =
        rootData.defaultSort;
    }

    return loadCompleteCollection(
        rootData.jsonUrl,
        loaderOptions
    );
    }


    function requestCollectionShell(
    url,
    options
    ) {
    options = options || {};

    var forceReload =
        options.forceReload === true;

    var parsedUrl;

    try {
        parsedUrl =
        new URL(
            url,
            window.location.origin
        );
    } catch (error) {
        return Promise.reject(error);
    }

    /*
        Include the query string so different Shopify
        sort states cannot share incorrect shell data.
    */
    var cacheKey =
        parsedUrl.pathname +
        parsedUrl.search;

    if (
        !forceReload &&
        collectionShellCache[cacheKey]
    ) {
        return Promise.resolve(
        collectionShellCache[cacheKey]
        );
    }

    if (
        !forceReload &&
        collectionShellLoadingPromises[
        cacheKey
        ]
    ) {
        return collectionShellLoadingPromises[
        cacheKey
        ];
    }

    var loadingPromise =
        window.fetch(
        parsedUrl.toString(),
        {
            method: "GET",
            credentials: "same-origin",
            headers: {
            Accept: "text/html"
            }
        }
        )
        .then(function(response) {
            if (!response.ok) {
            throw new Error(
                "Could not load the collection page. " +
                "Shopify returned HTTP " +
                response.status +
                "."
            );
            }

            return response.text();
        })
        .then(function(html) {
            var parser =
            new DOMParser();

            var destinationDocument =
            parser.parseFromString(
                html,
                "text/html"
            );

            collectionShellCache[
            cacheKey
            ] = destinationDocument;

            return destinationDocument;
        })
        .finally(function() {
            delete collectionShellLoadingPromises[
            cacheKey
            ];
        });

    collectionShellLoadingPromises[
        cacheKey
    ] = loadingPromise;

    return loadingPromise;
    }


    function replaceCollectionShellRegions(
    currentRoot,
    destinationDocument
    ) {
    if (
        !currentRoot ||
        !destinationDocument
    ) {
        return;
    }

    var currentRegions =
        Array.prototype.slice.call(
        currentRoot.querySelectorAll(
            "[data-singles-shell]"
        )
        );

    destinationDocument
        .querySelectorAll(
        "[data-singles-shell]"
        )
        .forEach(function(destinationRegion) {
        var regionName =
            normalizeText(
            destinationRegion.getAttribute(
                "data-singles-shell"
            )
            );

        if (!regionName) {
            return;
        }

        var currentRegion =
            currentRegions.find(
            function(candidate) {
                return (
                normalizeText(
                    candidate.getAttribute(
                    "data-singles-shell"
                    )
                ) === regionName
                );
            }
            );

        if (!currentRegion) {
            return;
        }

        currentRegion.innerHTML =
            destinationRegion.innerHTML;
        });
    }


    function createCollectionNavigation(
    config
    ) {
    config =
        Object.assign(
        {
            root:
            "[data-singles-collection]",

            rootSelector:
            "[data-singles-collection]",

            linkSelector:
            "[data-singles-collection-link]",

            activeClass:
            "active",

            loadingClass:
            "is-loading",

            gridInstance: null,
            preserveSortOnLinks: true,
            reloadOnSort: true,

            loaderOptions: {},

            fallbackOnError: true
        },
        config || {}
        );

    var root =
        typeof config.root === "string"
        ? document.querySelector(
            config.root
            )
        : config.root;

    if (!root) {
        console.warn(
        "Shared collection navigation root was not found."
        );

        return null;
    }

    if (
        root._singlesCollectionNavigation
    ) {
        return root
        ._singlesCollectionNavigation;
    }

    var navigationRequestId = 0;
    var eventsBound = false;

    function getCurrentSortValue() {
      if (
        !config.gridInstance ||
        !config.gridInstance.state
      ) {
        return "";
      }

      return normalizeText(
        config.gridInstance.state.sort
      );
    }

    function prepareDestinationUrl(
        rawUrl
    ) {
        var destinationUrl =
        new URL(
            rawUrl,
            window.location.origin
        );

        var sortValue =
          getCurrentSortValue();

        if (sortValue) {
          destinationUrl
            .searchParams
            .set(
              "sort_by",
              sortValue
            );

          destinationUrl
            .searchParams
            .delete(
              "page"
            );
        }

        return destinationUrl;
    }


    function getLoaderOptions(
        additionalOptions
    ) {
        var loaderOptions =
        Object.assign(
            {},
            config.loaderOptions ||
            {},
            additionalOptions ||
            {}
        );

        var sortValue =
          getCurrentSortValue();

        if (sortValue) {
          loaderOptions.serverSort =
            sortValue;
        }

        return loaderOptions;
    }

    function syncCollectionLinkSort() {
      if (
        config.preserveSortOnLinks ===
        false
      ) {
        return;
      }

      var sortValue =
        getCurrentSortValue();

      if (!sortValue) {
        return;
      }

      root
        .querySelectorAll(
          config.linkSelector
        )
        .forEach(function(link) {
          try {
            var linkUrl =
              new URL(
                link.href,
                window.location.origin
              );

            linkUrl
              .searchParams
              .set(
                "sort_by",
                sortValue
              );

            linkUrl
              .searchParams
              .delete(
                "page"
              );

            link.href =
              linkUrl.toString();
          } catch (error) {
            /* Keep original link. */
          }
        });
    }

    function handleSortChange(
      event
    ) {
      if (
        config.reloadOnSort ===
          false ||
        !config.gridInstance ||
        !config.gridInstance.root
      ) {
        return;
      }

      var sortSelector =
        config.gridInstance.config
          .sortControl;

      if (
        !sortSelector ||
        !event.target.matches(
          sortSelector
        )
      ) {
        return;
      }

      syncCollectionLinkSort();

      loadCurrent()
        .catch(function(error) {
          console.error(
            "Unable to reload singles collection after sorting:",
            error
          );
        });
    }

    function showNavigationError() {
      if (
        config.gridInstance &&
        typeof config.gridInstance.showError ===
          "function"
      ) {
        config.gridInstance.showError(
          "Please refresh the page and try again."
        );
      }
    }

    function renderNavigationSkeleton() {
      if (
        !config.gridInstance ||
        !config.gridInstance.grid
      ) {
        return;
      }

      renderSkeletonIntoGrid(
        config.gridInstance.grid,
        config.gridInstance.state.view
      );
    }


    function setNavigationLoading(
        isLoading
    ) {
        root.classList.toggle(
        "is-loading-full-set",
        Boolean(isLoading)
        );
    }


    function updateActiveLinks(
        destinationHandle,
        isLoading
    ) {
        root
        .querySelectorAll(
            config.linkSelector
        )
        .forEach(function(link) {
            var linkHandle =
            getCollectionHandleFromUrl(
                link.href
            );

            var active =
            linkHandle ===
            destinationHandle;

            link.classList.toggle(
            config.activeClass,
            active
            );

            link.classList.toggle(
            config.loadingClass,
            active &&
            Boolean(isLoading)
            );

            if (active) {
            link.setAttribute(
                "aria-current",
                "page"
            );
            } else {
            link.removeAttribute(
                "aria-current"
            );
            }
        });
    }


    function applyDestinationShell(
        destinationDocument
    ) {
        var destinationRoot =
        destinationDocument
            .querySelector(
            config.rootSelector
            );

        if (!destinationRoot) {
        throw new Error(
            "The destination collection root was not found."
        );
        }

        replaceCollectionShellRegions(
        root,
        destinationDocument
        );

        syncCollectionLinkSort();

        copyCollectionRootData(
        root,
        destinationRoot
        );

        if (
        destinationDocument.title
        ) {
        document.title =
            destinationDocument.title;
        }

        if (
          config.gridInstance &&
          typeof config.gridInstance
            .centerActiveFamilyTab ===
            "function"
        ) {
          window.requestAnimationFrame(
            function() {
              config.gridInstance
                .centerActiveFamilyTab();
            }
          );
        }
    }


    function installRows(
      rows,
      requestId
    ) {
      if (
        requestId !==
        navigationRequestId
      ) {
        return Promise.resolve(rows);
      }

      rows =
        Array.isArray(rows)
          ? rows
          : [];

      if (
        config.gridInstance &&
        typeof config.gridInstance.setRows ===
          "function"
      ) {
        config.gridInstance.setRows(
          rows
        );
      }

      return Promise
        .resolve(rows)
        .then(function() {
          if (
            requestId ===
            navigationRequestId
          ) {
            setNavigationLoading(
              false
            );
          }

          return rows;
        });
    }


    function loadCurrent(
        options
    ) {
        options = options || {};

        navigationRequestId += 1;

        var requestId =
        navigationRequestId;

        setNavigationLoading(true);

        renderNavigationSkeleton();

        return loadCollectionRoot(
        root,
        getLoaderOptions(
            {
            forceReload:
                options.forceReload ===
                true
            }
        )
        )
        .then(function(rows) {
            return installRows(
              rows,
              requestId
            );
        })
        .catch(function(error) {
            if (
            requestId !==
            navigationRequestId
            ) {
            return [];
            }

            setNavigationLoading(false);

            console.error(
              "Unable to load singles collection:",
              error
            );

            showNavigationError();

            throw error;
        });
    }


    function navigate(
        rawUrl,
        options
    ) {
        options = options || {};

        var destinationUrl =
        prepareDestinationUrl(
            rawUrl
        );

        var destinationHandle =
        getCollectionHandleFromUrl(
            destinationUrl.toString()
        );

        if (!destinationHandle) {
        window.location.href =
            destinationUrl.toString();

        return Promise.resolve([]);
        }

        navigationRequestId += 1;

        var requestId =
        navigationRequestId;

        setNavigationLoading(true);

        updateActiveLinks(
          destinationHandle,
          true
        );

        renderNavigationSkeleton();

        return requestCollectionShell(
        destinationUrl.toString(),
        {
            forceReload:
            options.forceReload ===
            true
        }
        )
        .then(function(
            destinationDocument
        ) {
            if (
            requestId !==
            navigationRequestId
            ) {
            return [];
            }

            applyDestinationShell(
            destinationDocument
            );

            updateActiveLinks(
            destinationHandle,
            true
            );

            if (
            options.history ===
            "replace"
            ) {
            window.history.replaceState(
                {
                singlesCollectionUrl:
                    destinationUrl.toString()
                },
                "",
                destinationUrl.toString()
            );
            } else if (
            options.history !==
            "none"
            ) {
            window.history.pushState(
                {
                singlesCollectionUrl:
                    destinationUrl.toString()
                },
                "",
                destinationUrl.toString()
            );
            }

            return loadCollectionRoot(
            root,
            getLoaderOptions(
                {
                forceReload:
                    options.forceReload ===
                    true
                }
            )
            );
        })
        .then(function(rows) {
            if (
            requestId !==
            navigationRequestId
            ) {
            return rows;
            }

            updateActiveLinks(
            destinationHandle,
            false
            );

            return installRows(
              rows,
              requestId
            );
        })
        .catch(function(error) {
            if (
            requestId !==
            navigationRequestId
            ) {
            return [];
            }

            setNavigationLoading(false);

            console.error(
              "Unable to navigate singles collection:",
              error
            );

            var shouldFallback =
              options.fallbackOnError !==
                false &&
              config.fallbackOnError !==
                false;

            if (shouldFallback) {
              window.location.href =
                destinationUrl.toString();

              return [];
            }

            showNavigationError();

            throw error;
        });
    }


    function handleDocumentClick(
        event
    ) {
        var link =
        event.target.closest(
            config.linkSelector
        );

        if (
        !link ||
        !root.contains(link)
        ) {
        return;
        }

        if (
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey ||
        event.button === 1 ||
        link.target === "_blank" ||
        link.hasAttribute("download")
        ) {
        return;
        }

        if (
        link.classList.contains(
            config.activeClass
        ) &&
        !link.classList.contains(
            config.loadingClass
        )
        ) {
        event.preventDefault();
        return;
        }

        event.preventDefault();

        navigate(
        link.href,
        {
            history: "push"
        }
        );
    }


    function handlePopState() {
        navigate(
        window.location.href,
        {
            history: "none",
            fallbackOnError: true
        }
        );
    }


    function bind() {
        if (eventsBound) {
        return;
        }

        eventsBound = true;

        document.addEventListener(
        "click",
        handleDocumentClick
        );

        window.addEventListener(
        "popstate",
        handlePopState
        );

        if (
          config.gridInstance &&
          config.gridInstance.root
        ) {
          config.gridInstance.root
            .addEventListener(
              "change",
              handleSortChange
            );
        }

        syncCollectionLinkSort();
    }


    function destroy() {
        if (!eventsBound) {
        return;
        }

        eventsBound = false;

        document.removeEventListener(
        "click",
        handleDocumentClick
        );

        window.removeEventListener(
        "popstate",
        handlePopState
        );

        delete root
        ._singlesCollectionNavigation;

        if (
          config.gridInstance &&
          config.gridInstance.root
        ) {
          config.gridInstance.root
            .removeEventListener(
              "change",
              handleSortChange
            );
        }
    }


    var navigation = {
        root: root,
        bind: bind,
        destroy: destroy,
        navigate: navigate,
        loadCurrent: loadCurrent
    };

    root._singlesCollectionNavigation =
        navigation;

    bind();

    return navigation;
    }

  /* ======================================================
     Card grouping and selected variants
  ====================================================== */

  function defaultCardKey(row) {
    return normalizeText(row && (row.handle || row.product_handle || row.product_id));
  }

  function groupRows(rows, config) {
    var grouped = {};
    var order = [];
    var getCardKey = typeof config.getCardKey === "function"
      ? config.getCardKey
      : defaultCardKey;

    rows.forEach(function (row, index) {
      var cardKey = normalizeText(getCardKey(row, index));

      if (!cardKey) {
        return;
      }

      if (!grouped[cardKey]) {
        grouped[cardKey] = {
          key: cardKey,
          sourceIndex: index,
          variants: []
        };
        order.push(cardKey);
      }

      grouped[cardKey].variants.push(row);
    });

    return order.map(function (cardKey) {
      var card = grouped[cardKey];

      card.variants.sort(function (a, b) {
        return conditionRank(a.condition) - conditionRank(b.condition);
      });

      return card;
    });
  }

  function defaultSelectVariant(card, state) {
    var variants = card && Array.isArray(card.variants)
      ? card.variants
      : [];

    var rememberedId = state.selectedVariantIds[card.key];

    if (rememberedId) {
      var remembered = variants.find(function (variant) {
        return normalizeText(variant.variant_id) === normalizeText(rememberedId);
      });

      if (remembered) {
        if (state.availability !== "instock" || isAvailable(remembered)) {
          return remembered;
        }
      }
    }

    return (
      variants.find(isAvailable) ||
      variants[0] ||
      null
    );
  }

  function cardHasStock(card) {
    return Boolean(
      card &&
      Array.isArray(card.variants) &&
      card.variants.some(isAvailable)
    );
  }

  function findVariant(card, variantId) {
    if (!card || !Array.isArray(card.variants)) {
      return null;
    }

    return card.variants.find(function (variant) {
      return normalizeText(variant.variant_id) === normalizeText(variantId);
    }) || null;
  }

  function getConditionState(
    variant,
    selectedVariant,
    availability
    ) {
    var available =
        isAvailable(variant);

    var active =
        normalizeText(
        variant &&
        variant.variant_id
        ) ===
        normalizeText(
        selectedVariant &&
        selectedVariant.variant_id
        );

    var disabled =
        availability === "instock" &&
        !available;

    return {
        available: available,
        active: active,
        disabled: disabled
    };
    }


    function applyConditionButtonState(
    button,
    variant,
    selectedVariant,
    availability
    ) {
    if (
        !button ||
        !variant
    ) {
        return;
    }

    var state =
        getConditionState(
        variant,
        selectedVariant,
        availability
        );

    button.classList.toggle(
        "active",
        state.active
    );

    button.classList.toggle(
        "is-out-of-stock",
        !state.available
    );

    button.classList.toggle(
        "is-stock-disabled",
        state.disabled
    );

    button.disabled =
        state.disabled;

    button.setAttribute(
        "aria-pressed",
        state.active
        ? "true"
        : "false"
    );

    if (state.disabled) {
        button.setAttribute(
        "aria-disabled",
        "true"
        );

        button.setAttribute(
        "title",
        "Out of stock"
        );
    } else {
        button.removeAttribute(
        "aria-disabled"
        );

        button.setAttribute(
        "title",
        normalizeText(
            variant.condition
        )
        );
    }
    }

  /* ======================================================
     Configuration-driven filters
  ====================================================== */

  function normalizeFilterDefinition(definition) {
    /*
        A simple string definition continues to work:

        "rarity"

        It becomes the equivalent of:

        {
        key: "rarity",
        field: "rarity",
        label: "rarity"
        }
    */
    if (typeof definition === "string") {
        definition = {
        key: definition,
        field: definition,
        label: definition
        };
    }

    var normalized =
        Object.assign(
        {},
        definition || {}
        );

    normalized.key =
        normalizeText(
        normalized.key ||
        normalized.field
        );

    normalized.field =
        normalizeText(
        normalized.field ||
        normalized.key
        );

    normalized.label =
        normalizeText(
        normalized.label ||
        normalized.key
        );

    normalized.match =
        normalizeText(
        normalized.match ||
        "exact"
        ).toLowerCase();

    normalized.selection =
        normalizeText(
        normalized.selection ||
        "any"
        ).toLowerCase();

    /*
        Existing filters remain normal chip buttons unless
        a game explicitly requests another control.
    */
    normalized.control =
        normalizeText(
        normalized.control ||
        "chips"
        ).toLowerCase();

    if (
        normalized.control !== "chips" &&
        normalized.control !== "search-multiselect"
    ) {
        normalized.control = "chips";
    }

    normalized.placeholder =
        normalizeText(
        normalized.placeholder
        );

    normalized.emptyText =
        normalizeText(
        normalized.emptyText ||
        "No matching options"
        );

    normalized.options =
        uniqueValues(
        toArray(
            normalized.options
        )
            .map(function (value) {
                return normalizeText(value);
            })
            .filter(Boolean)
        );

    normalized.order =
        Array.isArray(
            normalized.order
        )
        ? normalized.order.slice()
        : [];

    /*
        A static options list also supplies the
        default display order unless the game
        explicitly provides another order.
    */
    if (
        !normalized.order.length &&
        normalized.options.length
    ) {
        normalized.order =
            normalized.options.slice();
    }

    normalized.maxSuggestions =
        Number(
        normalized.maxSuggestions
        );

    if (
        !Number.isFinite(
        normalized.maxSuggestions
        ) ||
        normalized.maxSuggestions < 1
    ) {
        normalized.maxSuggestions = 50;
    }

    normalized.initialSuggestions =
    Number(
        normalized.initialSuggestions
    );

    if (
    !Number.isFinite(
        normalized.initialSuggestions
    ) ||
    normalized.initialSuggestions < 1
    ) {
    normalized.initialSuggestions = 8;
    }

    normalized.initialSuggestions =
    Math.min(
        normalized.initialSuggestions,
        normalized.maxSuggestions
    );

    return normalized;
    }

  function normalizeFilterDefinitions(definitions) {
    return toArray(definitions)
      .map(normalizeFilterDefinition)
      .filter(function (definition) {
        return Boolean(definition.key);
      });
  }

  function splitFilterValue(value, definition) {
    if (Array.isArray(value)) {
      return value;
    }

    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return [];
    }

    if (definition.split === false) {
      return [value];
    }

    if (
      definition.separator instanceof RegExp
    ) {
      return String(value).split(
        definition.separator
      );
    }

    if (
      typeof definition.separator ===
      "string"
    ) {
      return String(value).split(
        definition.separator
      );
    }

    return [value];
  }

  function getCardFilterValues(card, definition, config) {
    if (typeof definition.getValues === "function") {
      return uniqueValues(toArray(definition.getValues(card, config)));
    }

    var values = [];

    card.variants.forEach(function (variant) {
      splitFilterValue(variant[definition.field], definition)
        .forEach(function (value) {
          var normalized = normalizeText(value);

          if (normalized) {
            values.push(normalized);
          }
        });
    });

    return uniqueValues(values);
  }

  function filterValueMatches(
    cardValue,
    selectedValue,
    definition
  ) {
    var cardText =
      normalizeLower(cardValue);

    var selectedText =
      normalizeLower(selectedValue);

    if (
      definition.match === "contains"
    ) {
      return (
        cardText.indexOf(
          selectedText
        ) !== -1
      );
    }

    if (
      definition.match === "starts-with"
    ) {
      return (
        cardText.indexOf(
          selectedText
        ) === 0
      );
    }

    return (
      cardText === selectedText
    );
  }

  function cardMatchesDefinition(card, definition, selectedValues, config) {
    if (!selectedValues.length) {
      return true;
    }

    var cardValues = getCardFilterValues(card, definition, config);

    if (definition.selection === "all") {
      return selectedValues.every(function (selectedValue) {
        return cardValues.some(function (cardValue) {
          return filterValueMatches(cardValue, selectedValue, definition);
        });
      });
    }

    return selectedValues.some(function (selectedValue) {
      return cardValues.some(function (cardValue) {
        return filterValueMatches(cardValue, selectedValue, definition);
      });
    });
  }

  function sortFilterValues(values, definition) {
    var orderLookup = {};

    definition.order.forEach(function (value, index) {
      orderLookup[normalizeLower(value)] = index;
    });

    return values.slice().sort(function (a, b) {
      var aKey = normalizeLower(a);
      var bKey = normalizeLower(b);
      var aRank = Object.prototype.hasOwnProperty.call(orderLookup, aKey)
        ? orderLookup[aKey]
        : Number.POSITIVE_INFINITY;
      var bRank = Object.prototype.hasOwnProperty.call(orderLookup, bKey)
        ? orderLookup[bKey]
        : Number.POSITIVE_INFINITY;

      if (aRank !== bRank) {
        return aRank - bRank;
      }

      return compareText(a, b);
    });
  }

  function getFilterOptionValues(
    instance,
    definition
  ) {
    var values = [];

    /*
      An explicit options list is the complete
      vocabulary for this filter.
    */
    if (
      Array.isArray(
        definition.options
      ) &&
      definition.options.length
    ) {
      values =
        definition.options.slice();

    /*
      Otherwise discover the available values
      from the cards in the current grid.
    */
    } else {
      instance.state.cards.forEach(
        function (card) {
          values =
            values.concat(
              getCardFilterValues(
                card,
                definition,
                instance.config
              )
            );
        }
      );
    }

    values =
      uniqueValues(values);

    if (
      typeof definition.filterOptions ===
        "function"
    ) {
      values =
        values.filter(
          function (value) {
            return definition.filterOptions(
              value,
              instance
            );
          }
        );
    }

    return sortFilterValues(
      values,
      definition
    );
  }

  function makeFilterControlId(
    instance,
    definition
) {
    var instanceName =
        normalizeText(
            instance.config.id ||
            instance.config.game ||
            "singles"
        )
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    var filterName =
        normalizeText(
            definition.key
        )
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    return (
        instanceName +
        "-" +
        filterName +
        "-suggestions"
    );
}


function renderSearchMultiselectControl(
    instance,
    definition,
    values
) {
    var selectedValues =
        instance.getFilterValues(
            definition.key
        );

    var suggestionsId =
        makeFilterControlId(
            instance,
            definition
        );

    var placeholder =
        definition.placeholder ||
        (
            "Search or select " +
            normalizeText(
                definition.label
            ).toLowerCase() +
            "..."
        );

    var selectedHtml =
        selectedValues.map(
            function (value) {
                return (
                    "<button" +
                        " type=\"button\"" +
                        " class=\"singles-search-multiselect-chip\"" +
                        " data-singles-search-remove" +
                        " data-singles-filter-key=\"" +
                            escapeAttribute(
                                definition.key
                            ) +
                        "\"" +
                        " data-singles-filter-value=\"" +
                            escapeAttribute(value) +
                        "\"" +
                        " aria-label=\"Remove " +
                            escapeAttribute(value) +
                        "\"" +
                    ">" +
                        "<span>" +
                            escapeHtml(value) +
                        "</span>" +
                        "<span" +
                            " class=\"singles-search-multiselect-chip-remove\"" +
                            " aria-hidden=\"true\"" +
                        ">" +
                            "×" +
                        "</span>" +
                    "</button>"
                );
            }
        ).join("");

    var optionsHtml =
        values.map(
            function (value) {
                var active =
                    selectedValues.some(
                        function (selected) {
                            return (
                                normalizeLower(
                                    selected
                                ) ===
                                normalizeLower(
                                    value
                                )
                            );
                        }
                    );

                return (
                    "<button" +
                        " type=\"button\"" +
                        " class=\"singles-search-multiselect-option" +
                            (
                                active
                                    ? " is-selected"
                                    : ""
                            ) +
                        "\"" +
                        " data-singles-search-option" +
                        " data-singles-filter-key=\"" +
                            escapeAttribute(
                                definition.key
                            ) +
                        "\"" +
                        " data-singles-filter-value=\"" +
                            escapeAttribute(value) +
                        "\"" +
                        " role=\"option\"" +
                        " aria-selected=\"" +
                            (
                                active
                                    ? "true"
                                    : "false"
                            ) +
                        "\"" +
                    ">" +
                        escapeHtml(value) +
                    "</button>"
                );
            }
        ).join("");

    return (
        "<div" +
            " class=\"singles-search-multiselect\"" +
            " data-singles-search-multiselect=\"" +
                escapeAttribute(
                    definition.key
                ) +
            "\"" +
        ">" +

            "<div" +
                " class=\"singles-search-multiselect-selected\"" +
                " data-singles-search-selected=\"" +
                    escapeAttribute(
                        definition.key
                    ) +
                "\"" +
                " aria-live=\"polite\"" +
            ">" +
                selectedHtml +
            "</div>" +

            "<div" +
                " class=\"singles-search-multiselect-wrap\"" +
            ">" +

                "<input" +
                    " type=\"search\"" +
                    " class=\"singles-search-multiselect-input\"" +
                    " data-singles-search-input=\"" +
                        escapeAttribute(
                            definition.key
                        ) +
                    "\"" +
                    " placeholder=\"" +
                        escapeAttribute(
                            placeholder
                        ) +
                    "\"" +
                    " autocomplete=\"off\"" +
                    " spellcheck=\"false\"" +
                    " role=\"combobox\"" +
                    " aria-autocomplete=\"list\"" +
                    " aria-expanded=\"false\"" +
                    " aria-controls=\"" +
                        escapeAttribute(
                            suggestionsId
                        ) +
                    "\"" +
                ">" +

                "<div" +
                    " id=\"" +
                        escapeAttribute(
                            suggestionsId
                        ) +
                    "\"" +
                    " class=\"singles-search-multiselect-suggestions\"" +
                    " data-singles-search-suggestions=\"" +
                        escapeAttribute(
                            definition.key
                        ) +
                    "\"" +
                    " role=\"listbox\"" +
                    " hidden" +
                ">" +

                    optionsHtml +

                    "<div" +
                        " class=\"singles-search-multiselect-empty\"" +
                        " data-singles-search-empty" +
                        " hidden" +
                    ">" +
                        escapeHtml(
                            definition.emptyText ||
                            "No matching options"
                        ) +
                    "</div>" +

                "</div>" +

            "</div>" +

        "</div>"
  );
}

  /* ======================================================
    Shared card rendering
    ====================================================== */

    function renderDefaultSkeleton(view) {
    var isListView =
        view === "list";

    /*
        Use a compact placeholder count for List
        and a fuller initial screen for Grid.
    */
    var skeletonCount =
        isListView
        ? 6
        : 12;

    var html = "";

    for (
        var index = 0;
        index < skeletonCount;
        index += 1
    ) {
        if (isListView) {
        html +=
            "<article" +
            " class=\"" +
                "singles-list-card " +
                "singles-skeleton-card" +
            "\"" +
            " aria-hidden=\"true\"" +
            ">";

        html +=
            "<div" +
            " class=\"singles-skeleton-image\"" +
            "></div>";

        html +=
            "<div" +
            " class=\"singles-skeleton-info\"" +
            ">" +
            "<span" +
                " class=\"" +
                "singles-skeleton-line " +
                "is-title" +
                "\"" +
            "></span>" +

            "<span" +
                " class=\"" +
                "singles-skeleton-line " +
                "is-detail" +
                "\"" +
            "></span>" +

            "<span" +
                " class=\"" +
                "singles-skeleton-line " +
                "is-set" +
                "\"" +
            "></span>" +

            "<span" +
                " class=\"" +
                "singles-skeleton-line " +
                "is-meta" +
                "\"" +
            "></span>" +
            "</div>";

        html +=
            "<div" +
            " class=\"singles-skeleton-price\"" +
            "></div>";

        html +=
            "<div" +
            " class=\"singles-skeleton-tabs\"" +
            ">" +
            "<span></span>" +
            "<span></span>" +
            "<span></span>" +
            "</div>";

        html +=
            "<div" +
            " class=\"singles-skeleton-purchase\"" +
            ">" +
            "<span" +
                " class=\"" +
                "singles-skeleton-line " +
                "is-stock" +
                "\"" +
            "></span>" +

            "<span" +
                " class=\"singles-skeleton-button\"" +
            "></span>" +
            "</div>";

        html += "</article>";
        } else {
        html +=
            "<article" +
            " class=\"" +
                "singles-grid-card " +
                "singles-skeleton-card" +
            "\"" +
            " aria-hidden=\"true\"" +
            ">";

        html +=
            "<div" +
            " class=\"singles-skeleton-grid-image\"" +
            "></div>";

        html +=
            "<div class=\"singles-grid-body\">" +

            "<span" +
                " class=\"" +
                "singles-skeleton-line " +
                "is-title" +
                "\"" +
            "></span>" +

            "<span" +
                " class=\"" +
                "singles-skeleton-line " +
                "is-detail" +
                "\"" +
            "></span>" +

            "<span" +
                " class=\"" +
                "singles-skeleton-line " +
                "is-set" +
                "\"" +
            "></span>" +

            "<div" +
                " class=\"singles-skeleton-grid-tabs\"" +
            ">" +
                "<span></span>" +
                "<span></span>" +
                "<span></span>" +
            "</div>" +

            "<div" +
                " class=\"singles-skeleton-grid-purchase\"" +
            ">" +
                "<span" +
                " class=\"" +
                    "singles-skeleton-line " +
                    "is-price" +
                "\"" +
                "></span>" +

                "<span" +
                " class=\"singles-skeleton-button\"" +
                "></span>" +
            "</div>" +

            "</div>";

        html += "</article>";
        }
    }

    return html;
    }

    function renderSkeletonIntoGrid(
    grid,
    view
    ) {
    if (!grid) {
        return "";
    }

    var normalizedView =
        view === "list"
        ? "list"
        : "grid";

    /*
        Apply the view before drawing the skeleton
        so Grid and List placeholders use the correct
        shared CSS immediately.
    */
    grid.classList.toggle(
        "is-grid-view",
        normalizedView === "grid"
    );

    grid.classList.toggle(
        "is-list-view",
        normalizedView === "list"
    );

    grid.setAttribute(
        "data-singles-view",
        normalizedView
    );

    grid.setAttribute(
        "aria-busy",
        "true"
    );

    var skeletonHtml =
      renderDefaultSkeleton(
        normalizedView
      );

    grid.innerHTML =
      skeletonHtml;

    return skeletonHtml;
    }

    function renderGridPurchase(
      variant,
      options
    ) {
      options = options || {};

      var available =
        isAvailable(
          variant
        );

      var html = "";

      html +=
        "<div" +
          " class=\"singles-grid-selected-area\"" +
          " data-singles-selected-area" +
          " data-selected-variant-id=\"" +
            escapeAttribute(
              variant &&
              variant.variant_id
            ) +
          "\"" +
        ">";

      if (available) {
        html +=
          "<div" +
            " class=\"singles-grid-price-row\"" +
          ">";

        html +=
          "<strong" +
            " class=\"singles-grid-price\"" +
          ">" +
            escapeHtml(
              getPriceDisplay(
                variant
              )
            ) +
          "</strong>";

        html += "</div>";

        html +=
          "<div" +
            " class=\"singles-grid-cart\"" +
          ">";

        html +=
          renderCartForm(
            variant,
            {
              mode: "grid"
            }
          );

        html += "</div>";
      } else {
        html +=
          "<div" +
            " class=\"singles-grid-price-row\"" +
          ">";

        html +=
          "<div" +
            " class=\"singles-grid-availability is-out\"" +
          ">" +
            "Availability: " +
            "<span>Out of Stock</span>" +
          "</div>";

        html += "</div>";

        html +=
          "<div" +
            " class=\"singles-grid-cart singles-grid-cart-notify\"" +
          ">";

        html +=
          renderNotifyButton(
            variant,
            {
              mode: "grid",

              formId:
                options.formId
            }
          );

        html +=
          renderWishlistButton(
            variant
          );

        html += "</div>";
      }

      html += "</div>";

      return html;
    }

    function shouldPrioritizeCardImage(
      card,
      instance
    ) {
      if (
        !instance ||
        !instance.state ||
        !Array.isArray(
          instance.state.visibleCards
        )
      ) {
        return false;
      }

      var count =
        Number(
          instance.config.eagerImageCount
        );

      if (
        !Number.isFinite(count) ||
        count < 1
      ) {
        count =
          DEFAULT_EAGER_IMAGE_COUNT;
      }

      return (
        instance.state.visibleCards
          .slice(
            0,
            Math.floor(count)
          )
          .indexOf(card) !== -1
      );
    }

    function renderGridImage(
    card,
    variant,
    options
    ) {
    options = options || {};

    var prioritizeImage =
      options.prioritize === true;

    var imageUrl =
        normalizeText(
        options.imageUrl ||
        (
            variant &&
            variant.img_url
        )
        );

    var productUrl =
        normalizeText(
        options.productUrl ||
        (
            variant &&
            variant.product_url
        )
        );

    if (
        !productUrl &&
        card &&
        card.key
    ) {
        productUrl =
        "/products/" +
        encodeURIComponent(
            card.key
        );
    }

    var imageAlt =
        normalizeText(
        options.imageAlt ||
        (
            variant &&
            variant.title
        ) ||
        ""
        );

    var html = "";

    html +=
        "<a" +
        " class=\"singles-grid-image\"" +
        " href=\"" +
            escapeAttribute(
            productUrl || "#"
            ) +
        "\"" +
        " data-singles-mobile-preview" +
        ">";

    if (imageUrl) {
        html +=
        "<img" +
            " src=\"" +
            escapeAttribute(
                imageUrl
            ) +
            "\"" +
            " alt=\"" +
            escapeAttribute(
                imageAlt
            ) +
            "\"" +
            " loading=\"" +
              (
                prioritizeImage
                  ? "eager"
                  : "lazy"
              ) +
            "\"" +
            (
              prioritizeImage
                ? " fetchpriority=\"high\""
                : ""
            ) +
            " decoding=\"async\"" +
            " data-singles-preview-source" +
        ">";
    } else {
        html +=
        "<div" +
            " class=\"singles-grid-image-placeholder\"" +
        ">" +
            "Image unavailable" +
        "</div>";
    }

    html += "</a>";

    return html;
    }


    function renderGridConditions(
      card,
      selected,
      instance
    ) {
      var variants =
        card &&
        Array.isArray(
          card.variants
        )
          ? card.variants
          : [];

      var availability =
        instance &&
        instance.state
          ? instance.state.availability
          : "all";

      var html = "";

      html +=
        "<div" +
          " class=\"singles-grid-tabs\"" +
          " data-singles-grid-conditions" +
        ">";

      variants.forEach(
        function(variant) {
          var state =
            getConditionState(
              variant,
              selected,
              availability
            );

          var classes = [
            "singles-grid-tab"
          ];

          if (state.active) {
            classes.push(
              "active"
            );
          }

          if (!state.available) {
            classes.push(
              "is-out-of-stock"
            );
          }

          if (state.disabled) {
            classes.push(
              "is-stock-disabled"
            );
          }

          html += "<button";
          html += " type=\"button\"";

          html +=
            " class=\"" +
              escapeAttribute(
                classes.join(" ")
              ) +
            "\"";

          html +=
            " data-singles-condition";

          html +=
            " data-variant-id=\"" +
              escapeAttribute(
                variant.variant_id
              ) +
            "\"";

          html +=
            " aria-pressed=\"" +
              (
                state.active
                  ? "true"
                  : "false"
              ) +
            "\"";

          if (state.disabled) {
            html += " disabled";
            html +=
              " aria-disabled=\"true\"";
            html +=
              " title=\"Out of stock\"";
          } else {
            html +=
              " title=\"" +
                escapeAttribute(
                  normalizeText(
                    variant.condition
                  )
                ) +
              "\"";
          }

          html += ">";

          html +=
            "<span" +
              " class=\"singles-condition-label\"" +
            ">" +
              escapeHtml(
                shortCondition(
                  variant.condition
                )
              ) +
            "</span>";

          html += "</button>";
        }
      );

      html += "</div>";

      html +=
        "<div" +
          " class=\"singles-grid-tab-line\"" +
          " aria-hidden=\"true\"" +
        "></div>";

      return html;
    }


    function renderGridCard(
    card,
    variant,
    instance,
    options
    ) {
    options = options || {};

    var cardKey =
        normalizeText(
        card &&
        card.key
        );

    var title =
        normalizeText(
        options.title ||
        (
            variant &&
            variant.title
        )
        );

    var productUrl =
        normalizeText(
        options.productUrl ||
        (
            variant &&
            variant.product_url
        )
        );

    if (
        !productUrl &&
        cardKey
    ) {
        productUrl =
        "/products/" +
        encodeURIComponent(
            cardKey
        );
    }

    var cardClasses = [
      "singles-grid-card"
    ];

    if (options.isFoil) {
      cardClasses.push(
        "is-foil"
      );
    }

    var titleClasses = [
        "singles-grid-title"
    ];

    if (options.titleClass) {
        titleClasses.push(
        normalizeText(
            options.titleClass
        )
        );
    }

    var html = "";

    html +=
        "<article" +
        " class=\"" +
            escapeAttribute(
            cardClasses.join(" ")
            ) +
        "\"" +
        " data-singles-card" +
        " data-singles-grid-card" +
        " data-card-key=\"" +
            escapeAttribute(
            cardKey
            ) +
        "\"" +
        " data-product-handle=\"" +
            escapeAttribute(
            cardKey
            ) +
        "\"" +
        " data-has-stock=\"" +
            (
            cardHasStock(card)
                ? "true"
                : "false"
            ) +
        "\"" +
        ">";

    html +=
        renderGridImage(
        card,
        variant,
        {
            imageUrl:
            options.imageUrl,

            productUrl:
            productUrl,

            imageAlt:
            options.imageAlt ||
            (
                variant &&
                variant.title
            ) ||
            title,

            prioritize:
            shouldPrioritizeCardImage(
              card,
              instance
            )
        }
        );

    html +=
      "<div" +
      " class=\"singles-grid-body\"" +
      ">";

    var titleRowClasses = [
    "singles-grid-title-row"
    ];

    if (options.titleRowClass) {
    titleRowClasses.push(
        normalizeText(
        options.titleRowClass
        )
    );
    }

    html +=
    "<div" +
        " class=\"" +
        escapeAttribute(
            titleRowClasses.join(" ")
        ) +
        "\"" +
    ">";

    html +=
        "<a" +
        " class=\"" +
            escapeAttribute(
            titleClasses.join(" ")
            ) +
        "\"" +
        " href=\"" +
            escapeAttribute(
            productUrl || "#"
            ) +
        "\"" +
        " data-singles-grid-title" +
        ">" +
        escapeHtml(title) +
        "</a>";

    html +=
        options.titleExtraHtml ||
        "";

    html +=
        options.finishBadgeHtml ||
        "";

    var dividerClasses = [
    "singles-grid-title-divider"
    ];

    if (options.titleDividerClass) {
    dividerClasses.push(
        normalizeText(
        options.titleDividerClass
        )
    );
    }

    html +=
    "<div" +
        " class=\"" +
        escapeAttribute(
            dividerClasses.join(" ")
        ) +
        "\"" +
        " aria-hidden=\"true\"" +
    "></div>";

    html += "</div>";

    html +=
        options.setRowHtml ||
        "";

    html +=
        renderGridConditions(
        card,
        variant,
        instance
        );

    html +=
    renderGridPurchase(
      variant,
      {
        formId:
          options.formId
      }
    );

    html += "</div>";
    html += "</article>";

    return html;
    }

    function renderListPrice(
    variant
    ) {

    var hasPrice =
        variant &&
        variant.price !== null &&
        variant.price !== undefined &&
        variant.price !== "";

    var priceText =
        hasPrice
        ? getPriceDisplay(variant)
        : "Unavailable";

    return (
        "<div" +
        " class=\"singles-list-price-area\"" +
        " data-singles-list-price-area" +
        ">" +
        "<strong" +
            " class=\"singles-list-price" +
            (!hasPrice ? " is-unavailable" : "") +
            "\"" +
        ">" +
            escapeHtml(priceText) +
        "</strong>" +
        "</div>"
    );
    }

    function renderListImage(
    card,
    variant,
    options
    ) {
    options = options || {};

    var prioritizeImage =
      options.prioritize === true;

    var imageUrl = normalizeText(
        options.imageUrl ||
        (variant && variant.img_url)
    );

    var productUrl = normalizeText(
        options.productUrl ||
        (variant && variant.product_url)
    );

    if (
        !productUrl &&
        card &&
        card.key
    ) {
        productUrl =
        "/products/" +
        encodeURIComponent(card.key);
    }

    var imageAlt = normalizeText(
        options.imageAlt ||
        (variant && variant.title) ||
        ""
    );

    var cardName = normalizeText(
        options.cardName ||
        imageAlt ||
        "card"
    );

    var html = "";

    html +=
        "<a" +
        " class=\"singles-list-image\"" +
        " href=\"" +
            escapeAttribute(productUrl || "#") +
        "\"" +
        " data-singles-mobile-preview" +
        " aria-label=\"Enlarge " +
            escapeAttribute(cardName) +
        "\"" +
        ">";

    if (imageUrl) {
        html +=
        "<img" +
            " class=\"singles-list-image-thumb\"" +
            " src=\"" +
            escapeAttribute(imageUrl) +
            "\"" +
            " alt=\"" +
            escapeAttribute(imageAlt) +
            "\"" +
            " loading=\"" +
              (
                prioritizeImage
                  ? "eager"
                  : "lazy"
              ) +
            "\"" +
            (
              prioritizeImage
                ? " fetchpriority=\"high\""
                : ""
            ) +
            " decoding=\"async\"" +
            " data-singles-preview-source" +
        ">";

        html +=
        "<span" +
            " class=\"singles-list-image-preview\"" +
            " aria-hidden=\"true\"" +
        ">";

        html +=
        "<span" +
            " class=\"singles-list-image-preview-frame\"" +
        ">";

        html +=
        "<img" +
            " src=\"" +
            escapeAttribute(imageUrl) +
            "\"" +
            " alt=\"\"" +
            " loading=\"lazy\"" +
            " decoding=\"async\"" +
        ">";

        html += "</span>";
        html += "</span>";
    } else {
        html +=
        "<span class=\"singles-list-image-placeholder\">" +
            "Image unavailable" +
        "</span>";
    }

    html += "</a>";

    return html;
    }

    function renderListConditions(
    card,
    selected,
    instance
    ) {
    var variants =
        card &&
        Array.isArray(card.variants)
        ? card.variants
        : [];

    var availability =
        instance &&
        instance.state
        ? instance.state.availability
        : "all";

    var html = "";

    html +=
        "<div" +
        " class=\"singles-list-conditions\"" +
        " data-singles-list-conditions" +
        ">";

    variants.forEach(function (variant) {
        var state =
        getConditionState(
            variant,
            selected,
            availability
        );

        var classes = [
        "singles-list-condition"
        ];

        if (state.active) {
        classes.push(
            "active"
        );
        }

        if (!state.available) {
        classes.push(
            "is-out-of-stock"
        );
        }

        if (state.disabled) {
        classes.push(
            "is-stock-disabled"
        );
        }

        html +=
        "<button" +
            " type=\"button\"" +
            " class=\"" +
            classes.join(" ") +
            "\"" +
            " data-singles-condition" +
            " data-variant-id=\"" +
            escapeAttribute(
                variant.variant_id
            ) +
            "\"" +
            " aria-pressed=\"" +
            (
                state.active
                ? "true"
                : "false"
            ) +
            "\"" +
            (
            state.disabled
                ? (
                " disabled" +
                " aria-disabled=\"true\"" +
                " title=\"Out of stock\""
                )
                : (
                " title=\"" +
                    escapeAttribute(
                    normalizeText(
                        variant.condition
                    )
                    ) +
                "\""
                )
            ) +
        ">" +

            "<span" +
            " class=\"singles-list-condition-label\"" +
            ">" +
            escapeHtml(
                shortCondition(
                variant.condition
                )
            ) +
            "</span>" +

        "</button>";
    });

    html += "</div>";

    return html;
    }

    function renderListStock(
    variant
    ) {
    var quantity = getQuantity(
        variant
    );

    var available =
        isAvailable(variant) &&
        quantity > 0;

    return (
        "<div" +
        " class=\"singles-list-stock" +
            (!available ? " is-out" : "") +
        "\"" +
        " data-singles-list-stock" +
        ">" +

        "<strong" +
            " class=\"singles-list-stock-number\"" +
        ">" +
            escapeHtml(
            available
                ? quantity.toLocaleString()
                : "0"
            ) +
        "</strong>" +

        "<span" +
            " class=\"singles-list-stock-label\"" +
        ">" +
            "available" +
        "</span>" +

        "</div>"
    );
    }

    function renderWishlistButton(
    variant
    ) {

    var productId = normalizeText(
    variant &&
    (
      variant.id ||
      variant.product_id ||
      variant.productId
    )
    ).replace(
        /^gid:\/\/shopify\/Product\//,
        ""
    );

    var productUrl = normalizeText(
        variant &&
        variant.product_url
    );

    if (
        !productUrl &&
        variant &&
        variant.handle
    ) {
        productUrl =
        "/products/" +
        encodeURIComponent(
            variant.handle
        );
    }

    return (
        "<a" +
        " href=\"" +
            escapeAttribute(
            productUrl || "#"
            ) +
        "\"" +
        " class=\"" +
          "singles-grid-heart " +
          "singles-list-heart " +
          "t4s-pr-item-btn " +
          "t4s-pr-wishlist" +
        "\"" +
        " data-action-wishlist" +
        " data-singles-wishlist" +
        " data-id=\"" +
            escapeAttribute(productId) +
        "\"" +
        " data-product-id=\"" +
            escapeAttribute(productId) +
        "\"" +
        " rel=\"nofollow\"" +
        " aria-label=\"Add to Wishlist\"" +
        ">" +
        "<span class=\"t4s-svg-pr-icon\">" +
            "<svg" +
            " viewBox=\"0 0 24 24\"" +
            " aria-hidden=\"true\"" +
            ">" +
            "<use" +
                " xlink:href=\"#t4s-icon-wis\"" +
                " href=\"#t4s-icon-wis\"" +
            "></use>" +
            "</svg>" +
        "</span>" +
        "</a>"
    );
    }

    function renderQuantityControl(
    variant,
    options
    ) {
    options = options || {};

    var quantity = getQuantity(
        variant
    );

    var maximum =
        Number(options.maximum || quantity);

    if (
        !Number.isFinite(maximum) ||
        maximum < 1
    ) {
        maximum = 9999;
    }

    var wrapperClasses = [
        "t4s-quantity-wrapper",
        "t4s-product-form__qty",
        "singles-quantity-wrapper"
    ];

    if (options.mode === "list") {
        wrapperClasses.push(
        "singles-list-quantity"
        );
    }

    return (
        "<div" +
        " data-quantity-wrapper" +
        " class=\"" +
            wrapperClasses.join(" ") +
        "\"" +
        ">" +
        "<button" +
            " data-quantity-selector" +
            " data-decrease-qty" +
            " type=\"button\"" +
            " class=\"" +
            "t4s-quantity-selector " +
            "is--minus" +
            "\"" +
            " aria-label=\"Decrease quantity\"" +
        ">" +
            "−" +
        "</button>" +

        "<input" +
            " data-quantity-value" +
            " type=\"number\"" +
            " class=\"t4s-quantity-input\"" +
            " step=\"1\"" +
            " min=\"1\"" +
            " max=\"" +
            escapeAttribute(maximum) +
            "\"" +
            " name=\"quantity\"" +
            " value=\"1\"" +
            " size=\"4\"" +
            " pattern=\"[0-9]*\"" +
            " inputmode=\"numeric\"" +
            " aria-label=\"Quantity\"" +
        ">" +

        "<button" +
            " data-quantity-selector" +
            " data-increase-qty" +
            " type=\"button\"" +
            " class=\"" +
            "t4s-quantity-selector " +
            "is--plus" +
            "\"" +
            " aria-label=\"Increase quantity\"" +
        ">" +
            "+" +
        "</button>" +
        "</div>"
    );
    }

    function renderAddToCartButton(
      options
    ) {
      options = options || {};

      var buttonText =
        normalizeText(options.text) ||
        "Add to Cart";

      var classes = [
        "t4s-product-form__submit",
        "t4s-btn",
        "t4s-btn-base",
        "t4s-btn-style-default",
        "t4s-btn-color-primary",
        "t4s-w-100",
        "t4s-justify-content-center",
        "t4s-btn-loading__svg",
        "singles-add-to-cart"
      ];

      if (options.mode === "list") {
        classes.push(
          "singles-list-add-to-cart"
        );
      }

      return (
        "<button" +
          " type=\"submit\"" +
          " name=\"add\"" +
          " data-atc-form" +
          " class=\"" +
            classes.join(" ") +
          "\"" +
        ">" +

          "<span class=\"t4s-btn-atc_text\">" +
            escapeHtml(buttonText) +
          "</span>" +

          "<span" +
            " class=\"t4s-loading__spinner\"" +
            " hidden" +
          ">" +

            "<svg" +
              " width=\"16\"" +
              " height=\"16\"" +
              " hidden" +
              " class=\"t4s-svg-spinner\"" +
              " focusable=\"false\"" +
              " role=\"presentation\"" +
              " viewBox=\"0 0 66 66\"" +
              " xmlns=\"http://www.w3.org/2000/svg\"" +
            ">" +

              "<circle" +
                " class=\"t4s-path\"" +
                " fill=\"none\"" +
                " stroke-width=\"6\"" +
                " cx=\"33\"" +
                " cy=\"33\"" +
                " r=\"30\"" +
              "></circle>" +

            "</svg>" +

          "</span>" +

        "</button>"
      );
    }

    function renderNotifyButton(
    variant,
    options
    ) {
    options = options || {};

    var formId =
        normalizeText(options.formId) ||
        "W2nZC4";

    var buttonText =
        normalizeText(options.text) ||
        "Notify me when back in stock";

    var title =
        normalizeText(options.title) ||
        "Back in stock notifications are temporarily unavailable";

    var classes = [
        "singles-grid-notify",
        "singles-notify-button",
        "needsclick"
    ];

    if (options.mode === "list") {
        classes.push(
        "singles-list-notify"
        );
    }

    return (
        "<button" +
        " type=\"button\"" +
        " class=\"" +
            classes.join(" ") +
        "\"" +
        " title=\"" +
            escapeAttribute(title) +
        "\"" +
        " data-klaviyo-form-id=\"" +
            escapeAttribute(formId) +
        "\"" +
        " data-product-handle=\"" +
            escapeAttribute(
                variant && variant.handle
            ) +
        "\"" +
        " data-variant-id=\"" +
            escapeAttribute(
                variant && variant.variant_id
            ) +
        "\"" +
        ">" +
        escapeHtml(buttonText) +
        "</button>"
    );
    }

    function renderCartForm(
    variant,
    options
    ) {
    options = options || {};

    var mode =
        options.mode === "list"
        ? "list"
        : "grid";  

    var rowClasses = [];

    if (mode === "list") {
        rowClasses.push(
        "singles-list-cart-row"
        );
    } else {
        rowClasses.push(
        "t4s-d-flex",
        "t4s-flex-wrap",
        "singles-grid-cart-row"
        );
    }

    return (
        "<form" +
        " class=\"singles-kalles-cart-form t4s-form__product\"" +
        " action=\"/cart/add\"" +
        " method=\"post\"" +
        " enctype=\"multipart/form-data\"" +
        " data-type=\"add-to-cart-form\"" +
        " data-singles-cart-form" +
        ">" +

        "<input" +
            " name=\"id\"" +
            " value=\"" +
            escapeAttribute(
                variant && variant.variant_id
            ) +
            "\"" +
            " type=\"hidden\"" +
        ">" +

        "<div" +
            " class=\"t4s-product-form__buttons\"" +
            " style=\"--pr-btn-round:999px;\"" +
        ">" +

            "<div" +
            " class=\"" +
                rowClasses.join(" ") +
            "\"" +
            ">" +

            renderQuantityControl(
                variant,
                {
                mode: mode
                }
            ) +

            renderAddToCartButton(
                {
                mode: mode
                }
            ) +

            renderWishlistButton(
                variant
            ) +

            "</div>" +
        "</div>" +
        "</form>"
    );
    }

    function renderListPurchase(
    variant,
    options
    ) {
    options = options || {};

    var available =
        isAvailable(variant) &&
        getQuantity(variant) > 0;

    var html = "";

    html +=
        "<div" +
        " class=\"singles-list-purchase" +
            (!available ? " is-out-of-stock" : "") +
        "\"" +
        " data-singles-list-purchase" +
        " data-selected-variant-id=\"" +
            escapeAttribute(
            variant && variant.variant_id
            ) +
        "\"" +
        ">";

    html += renderListStock(
        variant
    );

    if (available) {
        html += renderCartForm(
        variant,
        {
            mode: "list"
        }
        );
    } else {
        html +=
        "<div" +
            " class=\"singles-list-notify-row\"" +
        ">" +

            renderNotifyButton(
            variant,
            {
                mode: "list",
                formId: options.formId
            }
            ) +

            renderWishlistButton(
            variant
            ) +

        "</div>";
    }

    html += "</div>";

    return html;
    }

    function renderListCard(
    card,
    variant,
    instance,
    options
    ) {
    options = options || {};

    var cardKey = normalizeText(
        card && card.key
    );

    var title = normalizeText(
        options.title ||
        (variant && variant.title)
    );

    var productUrl = normalizeText(
        options.productUrl ||
        (variant && variant.product_url)
    );

    if (
        !productUrl &&
        cardKey
    ) {
        productUrl =
        "/products/" +
        encodeURIComponent(cardKey);
    }

    var cardClasses = [
        "singles-list-card"
    ];

    if (options.isFoil) {
        cardClasses.push("is-foil");
    }

    var finishBadgeHtml =
        options.finishBadgeHtml || "";

    var titleDetailsHtml =
        options.titleDetailsHtml || "";

    var setHtml =
        options.setHtml || "";

    var metadataHtml =
        options.metadataHtml || "";

    var html = "";

    html +=
        "<article" +
        " class=\"" +
            escapeAttribute(
            cardClasses.join(" ")
            ) +
        "\"" +
        " data-singles-card" +
        " data-singles-list-card" +
        " data-card-key=\"" +
            escapeAttribute(cardKey) +
        "\"" +
        " data-product-handle=\"" +
            escapeAttribute(cardKey) +
        "\"" +
        " data-has-stock=\"" +
            (
            cardHasStock(card)
                ? "true"
                : "false"
            ) +
        "\"" +
        ">";

    /*
        Card image and desktop/mobile preview.
    */
    html += renderListImage(
        card,
        variant,
        {
        imageUrl:
            options.imageUrl ||
            (
            variant &&
            variant.img_url
            ),

        productUrl: productUrl,

        imageAlt:
            options.imageAlt ||
            (
            variant &&
            variant.title
            ) ||
            title,

        cardName: title,

        prioritize:
        shouldPrioritizeCardImage(
          card,
          instance
        )
        }
    );

    /*
        Game-specific identity information.
    */
    html +=
        "<div" +
        " class=\"singles-list-info\"" +
        " data-singles-list-info" +
        ">";

    html +=
        "<div class=\"singles-list-title-line\">";

    html +=
        "<a" +
        " class=\"singles-list-title\"" +
        " href=\"" +
            escapeAttribute(
            productUrl || "#"
            ) +
        "\"" +
        " data-singles-list-title" +
        ">" +
        escapeHtml(title) +
        "</a>";

    html += finishBadgeHtml;

    html += "</div>";

    if (titleDetailsHtml) {
        html +=
        "<div" +
            " class=\"singles-list-title-details\"" +
            " data-singles-list-title-details" +
        ">" +
            titleDetailsHtml +
        "</div>";
    }

    html +=
        "<div" +
        " class=\"singles-list-title-divider\"" +
        " aria-hidden=\"true\"" +
        "></div>";

    if (setHtml) {
        html +=
        "<div" +
            " class=\"singles-list-set\"" +
            " data-singles-list-set" +
        ">" +
            setHtml +
        "</div>";
    }

    if (metadataHtml) {
        html +=
        "<div" +
            " class=\"singles-list-meta\"" +
            " data-singles-list-meta" +
        ">" +
            metadataHtml +
        "</div>";
    }

    html += "</div>";

    /*
        Selected-condition price.
    */
    html += renderListPrice(
    variant
    );

    /*
        This wrapper behaves as a normal grid wrapper on mobile.

        On desktop, the shared CSS uses display: contents so the
        conditions and purchase box become direct grid items.
    */
    html +=
        "<div" +
        " class=\"singles-list-purchase-panel\"" +
        " data-singles-list-purchase-panel" +
        ">";

    html += renderListConditions(
        card,
        variant,
        instance
    );

    html += renderListPurchase(
        variant,
        {
        formId: options.formId
        }
    );

    html += "</div>";
    html += "</article>";

    return html;
    }

    function updateGridCardVariant(
      cardElement,
      variant,
      options
    ) {
      options = options || {};

      if (
        !cardElement ||
        !variant
      ) {
        return;
      }

      var purchaseArea =
        cardElement.querySelector(
          "[data-singles-selected-area]"
        );

      if (purchaseArea) {
        purchaseArea.outerHTML =
          renderGridPurchase(
            variant,
            {
              formId:
                options.formId
            }
          );
      }

      cardElement.setAttribute(
        "data-selected-variant-id",
        normalizeText(
          variant.variant_id
        )
      );
    }

    function updateListCardVariant(
    cardElement,
    variant,
    options
    ) {
    options = options || {};

    if (
        !cardElement ||
        !variant
    ) {
        return;
    }

    /*
        Replace the displayed price.
    */
    var priceArea =
        cardElement.querySelector(
        "[data-singles-list-price-area]"
        );

    if (priceArea) {
        priceArea.outerHTML =
        renderListPrice(
            variant
        );
    }

    /*
        Replace the stock, cart, wishlist,
        or Notify area.
    */
    var purchaseArea =
        cardElement.querySelector(
        "[data-singles-list-purchase]"
        );

    if (purchaseArea) {
        purchaseArea.outerHTML =
        renderListPurchase(
            variant,
            {
            formId:
                options.formId
            }
        );
    }

    /*
        Record the newly selected condition
        on the card element.
    */
    cardElement.setAttribute(
        "data-selected-variant-id",
        normalizeText(
        variant.variant_id
        )
    );
    }

  /* ======================================================
     Shared grid instance
  ====================================================== */

  function SinglesGridInstance(config) {
    this.config = Object.assign({
      game: "singles",
      storageNamespace: "singles",
      root: "[data-singles-collection]",
      grid: "[data-singles-grid]",
      loading: "[data-singles-grid-loading]",
      rowsElementId: "",
      stockButton: "[data-singles-stock-filter]",
      viewButton: "[data-singles-view]",
      sortControl: "[data-singles-sort]",
      nameInput: "[data-singles-name-filter]",
      nameClearButton: "[data-singles-name-clear]",
      clearFiltersButton: "[data-singles-clear-filters]",
      filterButton: "[data-singles-filter-key][data-singles-filter-value]",
      mobileCountButton: "[data-singles-mobile-count]",
      mobileFilterOpenButton: "[data-singles-filter-open]",
      mobileFilterCloseButton: "[data-singles-filter-close]",
      mobileFilterSidebar: "#singles-filter-sidebar",
      mobileFilterOverlay: "[data-singles-filter-overlay]",
      tabsScroll: "[data-singles-tabs-scroll]",
      tabsScrollLeftButton: "[data-singles-tabs-scroll-left]",
      tabsScrollRightButton: "[data-singles-tabs-scroll-right]",

      defaultAvailability: "instock",

      persistAvailability: true,

      defaultView: "grid",
      defaultSort: "title-ascending",

      /*
        Search-style grids can keep purchasable
        cards ahead of completely out-of-stock
        cards while Availability = All.
      */
      stockFirstWhenAll: false,

      /*
          Individual grids may opt out of storing
          their sort preference.
      */
      persistSort: true,

      allowedSorts: [
        "manual",
        "best-selling",
        "title-ascending",
        "price-ascending",
        "price-descending"
      ],
      pageSize: DEFAULT_PAGE_SIZE,
        maximumPages: DEFAULT_MAXIMUM_PAGES,

        maxConcurrentRequests:
        DEFAULT_MAX_CONCURRENT_REQUESTS,

        requestRetries:
        DEFAULT_REQUEST_RETRIES,

        retryDelay:
        DEFAULT_RETRY_DELAY,

        requestTimeout:
        DEFAULT_REQUEST_TIMEOUT,

        searchDelay: DEFAULT_SEARCH_DELAY,

        eagerImageCount:
          DEFAULT_EAGER_IMAGE_COUNT,

        syncSortToUrl: true,

      filterDefinitions: [],

      emptyMessage: "No matching cards found.",
      errorHeading: "Cards could not be loaded."
    }, config || {});

    this.config.filterDefinitions = normalizeFilterDefinitions(
      this.config.filterDefinitions
    );

    this.root = null;
    this.grid = null;
    this.loading = null;
    this.eventsBound = false;
    this.searchTimer = null;

    this.filterSidebar = null;
    this.filterOverlay = null;
    this.mobileFilterCloseTimer = null;
    this.tabsScroll = null;
    this.tabsScrollLeftButton = null;
    this.tabsScrollRightButton = null;
    this.tabsScrollFrame = null;
    this.tabsResizeObserver = null;

    this.state = {
      rows: [],
      cards: [],
      visibleCards: [],
      availability: this.getSavedAvailability(),
      view: this.getSavedView(),
      sort: this.getSavedSort(),
      nameSearch: "",
      filters: {},
      selectedVariantIds: {},
      loading: false
    };

    this.config.filterDefinitions.forEach(function (definition) {
      this.state.filters[definition.key] = [];
    }, this);
  }

  SinglesGridInstance.prototype.getStorageKey = function (name) {
    return this.config.storageNamespace + "Singles" + name;
  };

  SinglesGridInstance.prototype.getSavedAvailability =
    function () {
    /*
        Some grids, such as MTG collections, should
        reset availability on every full page load.

        Their state can still survive AJAX navigation
        because the same SinglesGrid instance remains alive.
    */
    if (
        this.config.persistAvailability ===
        false
    ) {
        return (
        this.config.defaultAvailability ===
        "instock"
            ? "instock"
            : "all"
        );
    }

    var saved =
        safeGetStoredValue(
        this.getStorageKey(
            "Availability"
        )
        );

    if (
        saved === "all" ||
        saved === "instock"
    ) {
        return saved;
    }

    return (
        this.config.defaultAvailability ===
        "instock"
        ? "instock"
        : "all"
    );
    };

  SinglesGridInstance.prototype.getSavedView = function () {
    var saved = safeGetStoredValue(this.getStorageKey("View"));

    if (saved === "grid" || saved === "list") {
      return saved;
    }

    return this.config.defaultView === "list" ? "list" : "grid";
  };

  SinglesGridInstance.prototype.getSavedSort = function () {
    var allowedSorts =
        this.config.allowedSorts;

    var urlSort =
        new URLSearchParams(
        window.location.search
        ).get("sort_by");

    var saved = null;

    if (
        this.config.persistSort !==
        false
    ) {
        saved =
        safeGetStoredValue(
            this.getStorageKey("Sort")
        );
    }

    var candidate =
        urlSort ||
        saved ||
        this.config.defaultSort;

    return allowedSorts.indexOf(
        candidate
    ) !== -1
        ? candidate
        : this.config.defaultSort;
    };

  SinglesGridInstance.prototype.resolveElements = function () {
    this.root = typeof this.config.root === "string"
      ? document.querySelector(this.config.root)
      : this.config.root;

    if (!this.root) {
      return false;
    }

    this.grid = typeof this.config.grid === "string"
      ? this.root.querySelector(this.config.grid) || document.querySelector(this.config.grid)
      : this.config.grid;

    this.loading =
    typeof this.config.loading === "string"
        ? (
        this.root.querySelector(
            this.config.loading
        ) ||
        document.querySelector(
            this.config.loading
        )
        )
        : this.config.loading;

    this.filterSidebar =
    this.root.querySelector(
        this.config.mobileFilterSidebar
    );

    this.filterOverlay =
    this.root.querySelector(
        this.config.mobileFilterOverlay
    );

    this.tabsScroll =
    this.root.querySelector(
        this.config.tabsScroll
    );

    this.tabsScrollLeftButton =
    this.root.querySelector(
        this.config.tabsScrollLeftButton
    );

    this.tabsScrollRightButton =
    this.root.querySelector(
        this.config.tabsScrollRightButton
    );

    return Boolean(this.grid);
  };

  SinglesGridInstance.prototype.setLoading = function (isLoading) {
    this.state.loading = Boolean(isLoading);

    if (this.root) {
      this.root.classList.toggle("is-loading-full-set", this.state.loading);
    }

    if (this.grid) {
      this.grid.setAttribute("aria-busy", this.state.loading ? "true" : "false");
    }

    if (this.loading) {
      this.loading.hidden = !this.state.loading;
    }
    
    };

  SinglesGridInstance.prototype.showError = function (message) {
    if (!this.grid) {
      return;
    }

    this.grid.setAttribute("aria-busy", "false");
    this.grid.innerHTML =
      "<div class=\"singles-grid-error\" role=\"alert\">" +
        "<strong>" + escapeHtml(this.config.errorHeading) + "</strong>" +
        "<p>" + escapeHtml(message) + "</p>" +
      "</div>";
  };

  SinglesGridInstance.prototype.getSelectedVariant = function (card) {
    return defaultSelectVariant(
      card,
      this.state
    );
  };

  SinglesGridInstance.prototype.getCardTitle = function (card) {
    var selected =
      this.getSelectedVariant(card) ||
      card.variants[0] ||
      {};

    return selected.title || "";
  };


  SinglesGridInstance.prototype.getCardSortPrice =
  function (card) {
    var selected =
      this.getSelectedVariant(card) ||
      (
        card &&
        Array.isArray(card.variants)
          ? card.variants[0]
          : null
      ) ||
      {};

    return selected.price;
  };


  SinglesGridInstance.prototype.compareStockPriority =
  function (a, b) {
      /*
        Collections sort every card together.

        Search grids may opt into keeping
        purchasable cards ahead of completely
        out-of-stock cards while viewing All.
      */
      if (
        this.config.stockFirstWhenAll !==
          true ||
        this.state.availability !==
          "all"
      ) {
        return 0;
      }

      var aHasStock =
        cardHasStock(a);

      var bHasStock =
        cardHasStock(b);

      if (
        aHasStock ===
        bHasStock
      ) {
        return 0;
      }

      return aHasStock
        ? -1
        : 1;
  };

  SinglesGridInstance.prototype.setRows = function (rows) {
    this.state.rows = Array.isArray(rows) ? rows.slice() : [];
    this.state.cards = groupRows(this.state.rows, this.config);

    if (
      this.state.availability === "instock" &&
      !this.state.cards.some(cardHasStock)
    ) {
      this.state.availability = "all";
    }

    this.renderFilterOptions();
    this.syncControls();
    this.render();

    dispatch("singles:grid:rows", {
      game: this.config.game,
      rows: this.state.rows,
      cards: this.state.cards,
      instance: this
    });
  };

  SinglesGridInstance.prototype.matchesName = function (card) {
    var search = normalizeCardSearchText(this.state.nameSearch);

    if (!search) {
      return true;
    }

    var title = stripTitleDetails(this.getCardTitle(card));
    return normalizeCardSearchText(title).indexOf(search) !== -1;
  };

  SinglesGridInstance.prototype.matchesConfiguredFilters = function (card) {
    return this.config.filterDefinitions.every(function (definition) {
      var selectedValues = this.state.filters[definition.key] || [];
      return cardMatchesDefinition(card, definition, selectedValues, this.config);
    }, this);
  };

  SinglesGridInstance.prototype.getFilteredCards = function () {
    var cards = this.state.cards.filter(function (card) {
      if (this.state.availability === "instock" && !cardHasStock(card)) {
        return false;
      }

      if (!this.matchesName(card)) {
        return false;
      }

      if (!this.matchesConfiguredFilters(card)) {
        return false;
      }

      return true;
    }, this);

    return this.sortCards(cards);
  };

  SinglesGridInstance.prototype.sortCards =
  function (cards) {
    var sortValue =
      this.state.sort;

    var output =
      cards.slice();

    var self = this;

    function stockPriority(a, b) {
      return self.compareStockPriority(
        a,
        b
      );
    }

    function sourceOrder(a, b) {
      return (
        a.sourceIndex -
        b.sourceIndex
      );
    }

    function titleAscending(a, b) {
      return compareText(
        self.getCardTitle(a),
        self.getCardTitle(b)
      );
    }

    function compareCardPrices(
      a,
      b,
      descending
    ) {
      var priceA =
        self.getCardSortPrice(a);

      var priceB =
        self.getCardSortPrice(b);

      var parsedA =
        parsePrice(priceA);

      var parsedB =
        parsePrice(priceB);

      var priceComparison =
        comparePrices(
          priceA,
          priceB,
          descending
        );

      if (priceComparison) {
        return priceComparison;
      }

      /*
        Completely out-of-stock cards have no
        available-condition sort price.

        If both cards lack a sort price, preserve
        their incoming/source order.
      */
      if (
        !Number.isFinite(parsedA) &&
        !Number.isFinite(parsedB)
      ) {
        return sourceOrder(
          a,
          b
        );
      }

      /*
        Real price ties sort by card name,
        then source order for stability.
      */
      return (
        titleAscending(a, b) ||
        sourceOrder(a, b)
      );
    }

    if (
      sortValue === "manual" ||
      sortValue === "best-selling"
    ) {
      output.sort(function(a, b) {
        return (
          stockPriority(a, b) ||
          sourceOrder(a, b)
        );
      });

      return output;
    }

    if (
      sortValue === "title-ascending"
    ) {
      output.sort(function(a, b) {
        return (
          stockPriority(a, b) ||
          titleAscending(a, b) ||
          sourceOrder(a, b)
        );
      });

      return output;
    }

    if (
      sortValue === "price-ascending"
    ) {
      output.sort(function(a, b) {
        return (
          stockPriority(a, b) ||
          compareCardPrices(
            a,
            b,
            false
          )
        );
      });

      return output;
    }

    if (
      sortValue === "price-descending"
    ) {
      output.sort(function(a, b) {
        return (
          stockPriority(a, b) ||
          compareCardPrices(
            a,
            b,
            true
          )
        );
      });

      return output;
    }

    /*
      Unknown/default sort falls back
      to normal A-Z.
    */
    output.sort(function(a, b) {
      return (
        stockPriority(a, b) ||
        titleAscending(a, b) ||
        sourceOrder(a, b)
      );
    });

    return output;
  };

  SinglesGridInstance.prototype.renderCard = function (card) {
    var selected = this.getSelectedVariant(card);
    var renderer = this.config.renderer || {};

    if (!selected) {
      return "";
    }

    this.state.selectedVariantIds[card.key] = normalizeText(selected.variant_id);

    if (this.state.view === "list" && typeof renderer.renderListCard === "function") {
      return renderer.renderListCard(card, selected, this);
    }

    if (typeof renderer.renderGridCard === "function") {
      return renderer.renderGridCard(card, selected, this);
    }

    return "";
  };

  SinglesGridInstance.prototype.render = function () {
    if (!this.grid) {
      return;
    }

    var cards = this.getFilteredCards();
    var self = this;

    this.state.visibleCards = cards;

    this.grid.classList.toggle("is-grid-view", this.state.view === "grid");
    this.grid.classList.toggle("is-list-view", this.state.view === "list");
    this.grid.setAttribute("data-singles-view", this.state.view);

    if (!cards.length) {
      this.grid.innerHTML =
        "<div class=\"singles-no-results\">" +
          escapeHtml(this.config.emptyMessage) +
        "</div>";

      this.grid.setAttribute("aria-busy", "false");
      this.updateVisibleCount();
      return;
    }

    this.grid.innerHTML = cards.map(function (card) {
      return self.renderCard(card);
    }).join("");

    this.grid.setAttribute("aria-busy", "false");
    this.initializeRenderedCards();
    this.updateVisibleCount();

    dispatch("singles:grid:rendered", {
      game: this.config.game,
      cards: cards,
      view: this.state.view,
      instance: this
    });
  };

  SinglesGridInstance.prototype.initializeRenderedCards =
  function () {
    this.initializeCardImages();

    this.syncRenderedConditionAvailability();


    /*
      Cart / popup / wishlist behavior belongs
      to the shared singles lifecycle.
    */
    initializeKallesButtons(
      this.grid
    );

    initializeThemeWishlist();

    refreshThemeWishlist();
  };

  SinglesGridInstance.prototype.initializeCardImages = function () {
    if (!this.grid) {
      return;
    }

    this.grid.querySelectorAll(
      ".singles-grid-image, .singles-list-image"
    ).forEach(function (imageWrap) {
      var image = imageWrap.querySelector("img");

      if (!image) {
        imageWrap.classList.add("is-image-loaded");
        return;
      }

      function markLoaded() {
        imageWrap.classList.add("is-image-loaded");
      }

      if (image.complete && image.naturalWidth > 0) {
        markLoaded();
      } else {
        image.addEventListener("load", markLoaded, { once: true });
        image.addEventListener("error", markLoaded, { once: true });
      }
    });
  };

  SinglesGridInstance.prototype.renderFilterOptions =
  function () {
    var self = this;

    this.config.filterDefinitions.forEach(
      function (definition) {
        var selector =
          "[data-singles-filter-options=\"" +
          escapeAttribute(
            definition.key
          ) +
          "\"]";

        var container =
          self.root.querySelector(
            selector
          );

        if (!container) {
          return;
        }

        var values =
          getFilterOptionValues(
            self,
            definition
          );

        /*
        Keep every configured filter visible, even when the
        current collection has no available values.

        Searchable controls will still render their input,
        but their suggestion list will contain no options.
        */

        container.hidden = false;

        /*
          Searchable multiselect controls use the
          shared component renderer.
        */
        if (
          definition.control ===
          "search-multiselect"
        ) {
          container.innerHTML =
            renderSearchMultiselectControl(
              self,
              definition,
              values
            );

          return;
        }

        /*
          Every other definition continues using
          the existing chip-button behavior.
        */
        container.innerHTML =
          values.map(
            function (value) {
              var active =
                (
                  self.state.filters[
                    definition.key
                  ] ||
                  []
                ).some(
                  function (selected) {
                    return (
                      normalizeLower(
                        selected
                      ) ===
                      normalizeLower(
                        value
                      )
                    );
                  }
                );

              if (
                typeof definition.renderOption ===
                "function"
              ) {
                return definition.renderOption(
                  value,
                  active,
                  self
                );
              }

              return (
                "<button" +
                  " type=\"button\"" +
                  " class=\"singles-filter-chip" +
                    (
                      active
                        ? " active"
                        : ""
                    ) +
                  "\"" +
                  " data-singles-filter-key=\"" +
                    escapeAttribute(
                      definition.key
                    ) +
                  "\"" +
                  " data-singles-filter-value=\"" +
                    escapeAttribute(
                      value
                    ) +
                  "\"" +
                  " aria-pressed=\"" +
                    (
                      active
                        ? "true"
                        : "false"
                    ) +
                  "\"" +
                ">" +
                  escapeHtml(value) +
                "</button>"
              );
            }
          ).join("");
      }
    );
  };

  SinglesGridInstance.prototype.getSearchMultiselectControl =
  function (element) {
    return element
      ? element.closest(
          "[data-singles-search-multiselect]"
        )
      : null;
  };


SinglesGridInstance.prototype.closeSearchMultiselect =
  function (control) {
    if (!control) {
      return;
    }

    var input = control.querySelector(
      "[data-singles-search-input]"
    );

    var suggestions = control.querySelector(
      "[data-singles-search-suggestions]"
    );

    if (suggestions) {
      suggestions.hidden = true;
    }

    if (input) {
      input.setAttribute(
        "aria-expanded",
        "false"
      );

      input.removeAttribute(
        "aria-activedescendant"
      );
    }

    control.querySelectorAll(
      "[data-singles-search-option]"
    ).forEach(function (option) {
      option.classList.remove(
        "is-active"
      );
    });

    control._singlesActiveOptionIndex = -1;
  };


SinglesGridInstance.prototype.closeAllSearchMultiselects =
  function (exceptControl) {
    if (!this.root) {
      return;
    }

    var self = this;

    this.root.querySelectorAll(
      "[data-singles-search-multiselect]"
    ).forEach(function (control) {
      if (control !== exceptControl) {
        self.closeSearchMultiselect(
          control
        );
      }
    });
  };


SinglesGridInstance.prototype.updateSearchMultiselectSuggestions =
  function (input) {
    var control =
      this.getSearchMultiselectControl(
        input
      );

    if (!control) {
      return [];
    }

    var key = normalizeText(
      input.getAttribute(
        "data-singles-search-input"
      )
    );

    var definition =
      this.getFilterDefinition(key);

    var suggestions =
      control.querySelector(
        "[data-singles-search-suggestions]"
      );

    var emptyMessage =
      control.querySelector(
        "[data-singles-search-empty]"
      );

    if (
      !definition ||
      !suggestions
    ) {
      return [];
    }

    var query =
      normalizeLower(input.value);

    var selectedValues =
      this.getFilterValues(key);

    var maximum =
      Number(
        definition.maxSuggestions
      );

    if (
      !Number.isFinite(maximum) ||
      maximum < 1
    ) {
      maximum = 50;
    }

    var suggestionLimit =
    query
        ? maximum
        : Number(
            definition.initialSuggestions ||
            8
        );

    var visibleOptions = [];

    suggestions.querySelectorAll(
      "[data-singles-search-option]"
    ).forEach(function (option) {
      var value = normalizeText(
        option.getAttribute(
          "data-singles-filter-value"
        )
      );

      var alreadySelected =
        selectedValues.some(
          function (selected) {
            return (
              normalizeLower(selected) ===
              normalizeLower(value)
            );
          }
        );

      var matchesQuery =
        !query ||
        normalizeLower(value).indexOf(
          query
        ) !== -1;

      var shouldShow =
        !alreadySelected &&
        matchesQuery &&
        visibleOptions.length <
        suggestionLimit;

      option.hidden = !shouldShow;

      option.classList.remove(
        "is-active"
      );

      if (shouldShow) {
        visibleOptions.push(option);
      }
    });

    control._singlesActiveOptionIndex = -1;

    input.removeAttribute(
      "aria-activedescendant"
    );

    if (emptyMessage) {
      emptyMessage.hidden =
        visibleOptions.length > 0;
    }

    return visibleOptions;
  };


SinglesGridInstance.prototype.openSearchMultiselect =
  function (input) {
    var control =
      this.getSearchMultiselectControl(
        input
      );

    if (!control) {
      return;
    }

    this.closeAllSearchMultiselects(
      control
    );

    var suggestions =
      control.querySelector(
        "[data-singles-search-suggestions]"
      );

    if (!suggestions) {
      return;
    }

    suggestions.hidden = false;

    input.setAttribute(
      "aria-expanded",
      "true"
    );

    this.updateSearchMultiselectSuggestions(
      input
    );
  };


SinglesGridInstance.prototype.setSearchMultiselectActiveOption =
  function (
    input,
    nextIndex
  ) {
    var control =
      this.getSearchMultiselectControl(
        input
      );

    if (!control) {
      return;
    }

    var suggestions =
      control.querySelector(
        "[data-singles-search-suggestions]"
      );

    if (!suggestions) {
      return;
    }

    var visibleOptions =
      Array.prototype.filter.call(
        suggestions.querySelectorAll(
          "[data-singles-search-option]"
        ),
        function (option) {
          return !option.hidden;
        }
      );

    visibleOptions.forEach(
      function (option) {
        option.classList.remove(
          "is-active"
        );
      }
    );

    if (!visibleOptions.length) {
      control._singlesActiveOptionIndex = -1;

      input.removeAttribute(
        "aria-activedescendant"
      );

      return;
    }

    if (nextIndex < 0) {
      nextIndex =
        visibleOptions.length - 1;
    }

    if (
      nextIndex >=
      visibleOptions.length
    ) {
      nextIndex = 0;
    }

    var activeOption =
      visibleOptions[nextIndex];

    if (!activeOption.id) {
      activeOption.id =
        suggestions.id +
        "-option-" +
        nextIndex;
    }

    activeOption.classList.add(
      "is-active"
    );

    activeOption.scrollIntoView({
      block: "nearest"
    });

    control._singlesActiveOptionIndex =
      nextIndex;

    input.setAttribute(
      "aria-activedescendant",
      activeOption.id
    );
  };

  SinglesGridInstance.prototype.getFilterDefinition =
  function (key) {
    var normalizedKey =
      normalizeText(key);

    return (
      this.config.filterDefinitions.find(
        function (definition) {
          return (
            definition.key ===
            normalizedKey
          );
        }
      ) ||
      null
    );
  };


    SinglesGridInstance.prototype.getFilterValues =
    function (key) {
        var values =
        this.state.filters[
            normalizeText(key)
        ];

        return Array.isArray(values)
        ? values.slice()
        : [];
    };


    SinglesGridInstance.prototype.setFilterValues =
    function (
        key,
        values,
        options
    ) {
        options = options || {};

        var normalizedKey =
        normalizeText(key);

        var definition =
        this.getFilterDefinition(
            normalizedKey
        );

        if (!definition) {
        return;
        }

        var normalizedValues =
        uniqueValues(
            toArray(values)
            .map(function (value) {
                return normalizeText(value);
            })
            .filter(Boolean)
        );

        this.state.filters[
        normalizedKey
        ] = normalizedValues;

        if (options.sync !== false) {
        /*
            Rebuild both ordinary chips and searchable
            multiselect controls so their selected values
            stay synchronized.
        */
        this.renderFilterOptions();
        this.syncFilterButtons();
        }

        if (options.render !== false) {
        this.render();
        }

        dispatch(
        "singles:grid:filterchange",
        {
            game: this.config.game,
            key: normalizedKey,
            values:
            normalizedValues.slice(),
            instance: this
        }
        );
    };

  SinglesGridInstance.prototype.toggleFilterValue =
  function (key, value) {
    var selectedValues =
      this.getFilterValues(key);

    var normalizedValue =
      normalizeLower(value);

    var existingIndex = -1;

    selectedValues.some(
      function (
        selected,
        index
      ) {
        if (
          normalizeLower(selected) ===
          normalizedValue
        ) {
          existingIndex = index;
          return true;
        }

        return false;
      }
    );

    if (existingIndex >= 0) {
      selectedValues.splice(
        existingIndex,
        1
      );
    } else {
      selectedValues.push(value);
    }

    this.setFilterValues(
      key,
      selectedValues
    );
  };

  SinglesGridInstance.prototype.setAvailability =
    function (value) {
    this.state.availability =
        value === "instock"
        ? "instock"
        : "all";

    if (
        this.config.persistAvailability !==
        false
    ) {
        safeSetStoredValue(
        this.getStorageKey(
            "Availability"
        ),
        this.state.availability
        );
    }

    this.syncAvailabilityControls();
    this.render();
    };

  SinglesGridInstance.prototype.setView = function (value) {
    this.state.view = value === "list" ? "list" : "grid";
    safeSetStoredValue(this.getStorageKey("View"), this.state.view);
    this.syncViewControls();
    this.render();
  };

  SinglesGridInstance.prototype.setSort = function (value) {
    if (this.config.allowedSorts.indexOf(value) === -1) {
      value = this.config.defaultSort;
    }

    this.state.sort = value;

    if (
    this.config.persistSort !==
    false
    ) {
    safeSetStoredValue(
        this.getStorageKey("Sort"),
        value
    );
    }

    if (this.config.syncSortToUrl) {
      try {
        var nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set("sort_by", value);
        nextUrl.searchParams.delete("page");
        window.history.replaceState(window.history.state, "", nextUrl.toString());
      } catch (error) {
        /* Keep sorting functional if URL handling is unavailable. */
      }
    }

    this.syncSortControl();

    var self = this;
    var renderDelay =
      Number(
        this.config.sortRenderDelay || 0
      );

    if (renderDelay > 0) {
      window.clearTimeout(
        this.sortRenderTimer
      );

      this.sortRenderTimer =
        window.setTimeout(
          function() {
            self.sortRenderTimer = null;
            self.render();
          },
          renderDelay
        );

      return;
    }

    this.render();
  };

  SinglesGridInstance.prototype.setNameSearch =
  function (value) {
    this.state.nameSearch =
      String(
        value == null
          ? ""
          : value
      );

    this.syncNameControls();
    this.render();
  };

  SinglesGridInstance.prototype.clearFilters = function () {
    this.state.nameSearch = "";
    this.state.availability = this.config.clearAvailability || "all";

    Object.keys(this.state.filters).forEach(function (key) {
      this.state.filters[key] = [];
    }, this);

    if (
    this.config.persistAvailability !==
    false
    ) {
    safeSetStoredValue(
        this.getStorageKey(
        "Availability"
        ),
        this.state.availability
    );
    }

    this.renderFilterOptions();
    this.syncControls();
    this.render();
  };

  SinglesGridInstance.prototype.syncAvailabilityControls = function () {
    var self = this;

    this.root.querySelectorAll(this.config.stockButton)
      .forEach(function (button) {
        var active = button.getAttribute("data-singles-stock-filter") === self.state.availability;
        button.classList.toggle("active", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
      });
  };

  SinglesGridInstance.prototype.syncViewControls = function () {
    var self = this;

    this.root.querySelectorAll(this.config.viewButton)
      .forEach(function (button) {
        var active = button.getAttribute("data-singles-view") === self.state.view;
        button.classList.toggle("active", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
      });
  };

  SinglesGridInstance.prototype.syncSortControl = function () {
    var select = this.root.querySelector(this.config.sortControl);

    if (!select) {
      return;
    }

    var hasOption = Array.prototype.some.call(select.options, function (option) {
      return option.value === this.state.sort;
    }, this);

    if (hasOption) {
      select.value = this.state.sort;
    }
  };

  SinglesGridInstance.prototype.syncNameControls = function () {
    var input = this.root.querySelector(this.config.nameInput);
    var clearButton = this.root.querySelector(this.config.nameClearButton);

    if (input && input.value !== this.state.nameSearch) {
      input.value = this.state.nameSearch;
    }

    if (clearButton) {
    clearButton.hidden =
        !normalizeText(
        this.state.nameSearch
        );
    }
  };

  SinglesGridInstance.prototype.syncFilterButtons = function () {
    var self = this;

    this.root.querySelectorAll(this.config.filterButton)
      .forEach(function (button) {
        var key = button.getAttribute("data-singles-filter-key");
        var value = button.getAttribute("data-singles-filter-value");
        var active = (self.state.filters[key] || []).some(function (selected) {
          return normalizeLower(selected) === normalizeLower(value);
        });

        button.classList.toggle("active", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
      });
  };

  SinglesGridInstance.prototype.syncControls = function () {
    this.syncAvailabilityControls();
    this.syncViewControls();
    this.syncSortControl();
    this.syncNameControls();
    this.syncFilterButtons();
  };

  SinglesGridInstance.prototype.updateVisibleCount = function () {
    var count = this.state.visibleCards.length;
    var button = this.root.querySelector(this.config.mobileCountButton);

    if (button) {
      button.textContent = count === 1
        ? "Show 1 Card"
        : "Show " + count.toLocaleString() + " Cards";
    }

    var countTargets = this.root.querySelectorAll("[data-singles-result-count]");

    countTargets.forEach(function (target) {
      target.textContent = count.toLocaleString();
    });
  };

  SinglesGridInstance.prototype.handleConditionSelection = function (button) {
    var cardElement =
        button.closest(
        "[data-singles-card]"
        );

    if (!cardElement) {
        return;
    }

    var cardKey =
        cardElement.getAttribute(
        "data-card-key"
        ) ||
        cardElement.getAttribute(
        "data-handle"
        ) ||
        cardElement.getAttribute(
        "data-product-handle"
        ) ||
        "";

    var card =
        this.state.cards.find(
        function (candidate) {
            return (
            normalizeText(candidate.key) ===
            normalizeText(cardKey)
            );
        }
        );

    if (!card) {
        return;
    }

    var variant =
        findVariant(
        card,
        button.getAttribute(
            "data-variant-id"
        )
        );

    if (!variant) {
        return;
    }

    if (
        this.state.availability ===
        "instock" &&
        !isAvailable(variant)
    ) {
        return;
    }

    this.state.selectedVariantIds[
        card.key
    ] = normalizeText(
        variant.variant_id
    );

    var renderer =
        this.config.renderer || {};

    if (
        typeof renderer.updateCardVariant ===
        "function"
    ) {
        renderer.updateCardVariant(
        cardElement,
        card,
        variant,
        this
        );

        this.syncConditionStates(
        cardElement,
        card,
        variant
        );


        /*
          Variant changes replace the cart controls,
          so reconnect Kalles to the new markup.
        */
        initializeKallesButtons(
          cardElement
        );

        refreshThemeWishlist();

    } else {
        this.render();
    }
    };

  SinglesGridInstance.prototype.syncConditionStates =
    function (
    cardElement,
    card,
    selectedVariant
    ) {
    if (
        !cardElement ||
        !card
    ) {
        return;
    }

    var selected =
        selectedVariant ||
        this.getSelectedVariant(
        card
        );

    var availability =
        this.state.availability;

    cardElement
        .querySelectorAll(
        "[data-singles-condition]"
        )
        .forEach(function (button) {
        var variant =
            findVariant(
            card,
            button.getAttribute(
                "data-variant-id"
            )
            );

        if (!variant) {
            return;
        }

        applyConditionButtonState(
            button,
            variant,
            selected,
            availability
        );
        });
    };

  SinglesGridInstance.prototype.syncRenderedConditionAvailability =
  function () {
    if (!this.grid) {
      return;
    }

    var self = this;

    this.grid
      .querySelectorAll(
        "[data-singles-card]"
      )
      .forEach(function(cardElement) {
        var cardKey =
          cardElement.getAttribute(
            "data-card-key"
          ) ||
          cardElement.getAttribute(
            "data-product-handle"
          ) ||
          "";

        var card =
          self.state.cards.find(
            function(candidate) {
              return (
                normalizeText(
                  candidate.key
                ) ===
                normalizeText(
                  cardKey
                )
              );
            }
          );

        if (!card) {
          return;
        }

        self.syncConditionStates(
        cardElement,
        card
        );
      });
  };

  SinglesGridInstance.prototype.updateTabsScrollButtons =
  function () {

    if (!this.tabsScroll) {
      return;
    }

    var maximumScroll =
      Math.max(
        0,
        this.tabsScroll.scrollWidth -
        this.tabsScroll.clientWidth
      );

    var hasOverflow =
      maximumScroll > 2;

    var currentScroll =
      this.tabsScroll.scrollLeft;


    if (this.tabsScrollLeftButton) {

      this.tabsScrollLeftButton.hidden =
        !hasOverflow;

      var disableLeft =
        !hasOverflow ||
        currentScroll <= 2;

      this.tabsScrollLeftButton.disabled =
        disableLeft;

      this.tabsScrollLeftButton.setAttribute(
        "aria-disabled",
        disableLeft
          ? "true"
          : "false"
      );
    }


    if (this.tabsScrollRightButton) {

      this.tabsScrollRightButton.hidden =
        !hasOverflow;

      var disableRight =
        !hasOverflow ||
        currentScroll >=
          maximumScroll - 2;

      this.tabsScrollRightButton.disabled =
        disableRight;

      this.tabsScrollRightButton.setAttribute(
        "aria-disabled",
        disableRight
          ? "true"
          : "false"
      );
    }
  };


    SinglesGridInstance.prototype.scrollFamilyTabs =
    function (direction) {
    if (!this.tabsScroll) {
        return;
    }

    var scrollDistance =
        Math.max(
        140,
        this.tabsScroll.clientWidth * 0.72
        );

    this.tabsScroll.scrollBy({
        left: scrollDistance * direction,
        behavior: "smooth"
    });
    };


    SinglesGridInstance.prototype.centerActiveFamilyTab =
    function () {
    if (!this.tabsScroll) {
        return;
    }

    var activeTab =
        this.tabsScroll.querySelector(
        "a.active"
        );

    var maximumScroll =
        Math.max(
        0,
        this.tabsScroll.scrollWidth -
        this.tabsScroll.clientWidth
        );

    if (
        !activeTab ||
        maximumScroll <= 2
    ) {
        this.updateTabsScrollButtons();
        return;
    }

    var targetScroll =
        activeTab.offsetLeft -
        (
        this.tabsScroll.clientWidth -
        activeTab.offsetWidth
        ) / 2;

    this.tabsScroll.scrollLeft =
        Math.max(
        0,
        Math.min(
            targetScroll,
            maximumScroll
        )
        );

    this.updateTabsScrollButtons();
    };

  SinglesGridInstance.prototype.openMobileFilters =
  function () {
    if (
      !this.filterSidebar ||
      !this.filterOverlay
    ) {
      return;
    }

    window.clearTimeout(
      this.mobileFilterCloseTimer
    );

    this.filterOverlay.hidden = false;

    this.filterSidebar.setAttribute(
      "aria-hidden",
      "false"
    );

    document.body.classList.add(
      "singles-filters-open"
    );

    this.root.querySelectorAll(
      this.config.mobileFilterOpenButton
    ).forEach(function (button) {
      button.setAttribute(
        "aria-expanded",
        "true"
      );
    });

    var sidebar =
      this.filterSidebar;

    var overlay =
      this.filterOverlay;

    window.requestAnimationFrame(
      function () {
        sidebar.classList.add(
          "is-open"
        );

        overlay.classList.add(
          "is-open"
        );
      }
    );

    dispatch(
      "singles:filters:opened",
      {
        game: this.config.game,
        instance: this
      }
    );
  };


SinglesGridInstance.prototype.closeMobileFilters =
  function () {
    if (
      !this.filterSidebar ||
      !this.filterOverlay
    ) {
      return;
    }

    window.clearTimeout(
      this.mobileFilterCloseTimer
    );

    this.filterSidebar.classList.remove(
      "is-open"
    );

    this.filterOverlay.classList.remove(
      "is-open"
    );

    this.filterSidebar.setAttribute(
      "aria-hidden",
      "true"
    );

    document.body.classList.remove(
      "singles-filters-open"
    );

    this.root.querySelectorAll(
      this.config.mobileFilterOpenButton
    ).forEach(function (button) {
      button.setAttribute(
        "aria-expanded",
        "false"
      );
    });

    var overlay =
      this.filterOverlay;

    this.mobileFilterCloseTimer =
      window.setTimeout(
        function () {
          if (
            !overlay.classList.contains(
              "is-open"
            )
          ) {
            overlay.hidden = true;
          }
        },
        240
      );

    dispatch(
      "singles:filters:closed",
      {
        game: this.config.game,
        instance: this
      }
    );
  };

  SinglesGridInstance.prototype.bindEvents =
  function () {
    if (
      this.eventsBound ||
      !this.root
    ) {
      return;
    }

    this.eventsBound = true;

    var self = this;

    this.root.addEventListener(
      "keydown",
      function (event) {
        var input =
          event.target.closest(
            "[data-singles-search-input]"
          );

        if (
          !input ||
          !self.root.contains(input)
        ) {
          return;
        }

        var control =
          self.getSearchMultiselectControl(
            input
          );

        if (!control) {
          return;
        }

        if (event.key === "ArrowDown") {
          event.preventDefault();

          self.openSearchMultiselect(
            input
          );

          var currentIndex =
            Number(
                control._singlesActiveOptionIndex
            );

            if (!Number.isFinite(currentIndex)) {
            currentIndex = -1;
            }

            self.setSearchMultiselectActiveOption(
            input,
            currentIndex + 1
            );

          return;
        }

        if (event.key === "ArrowUp") {
          event.preventDefault();

          self.openSearchMultiselect(
            input
          );

          var currentIndex =
            Number(
              control._singlesActiveOptionIndex
            );

          if (
            !Number.isFinite(
              currentIndex
            )
          ) {
            currentIndex = 0;
          }

          self.setSearchMultiselectActiveOption(
            input,
            currentIndex - 1
          );

          return;
        }

        if (event.key === "Enter") {
          var activeOption =
            control.querySelector(
              "[data-singles-search-option].is-active"
            );

          if (activeOption) {
            event.preventDefault();
            activeOption.click();
          }

          return;
        }

        if (event.key === "Escape") {
          event.preventDefault();

          self.closeSearchMultiselect(
            control
          );

          return;
        }

        if (
          event.key === "Backspace" &&
          !input.value
        ) {
          var key =
            input.getAttribute(
              "data-singles-search-input"
            );

          var selectedValues =
            self.getFilterValues(key);

          if (selectedValues.length) {
            event.preventDefault();

            self.toggleFilterValue(
              key,
              selectedValues[
                selectedValues.length - 1
              ]
            );
          }
        }
      }
    );

    document.addEventListener(
      "click",
      function (event) {
        if (
          !event.target.closest(
            "[data-singles-search-multiselect]"
          )
        ) {
          self.closeAllSearchMultiselects();
        }
      }
    );

    this.root.addEventListener("click", function (event) {

        var tabsScrollLeftButton =
        event.target.closest(
            self.config.tabsScrollLeftButton
        );

        if (
        tabsScrollLeftButton &&
        self.root.contains(
            tabsScrollLeftButton
        )
        ) {
        event.preventDefault();

        self.scrollFamilyTabs(-1);

        return;
        }


        var tabsScrollRightButton =
        event.target.closest(
            self.config.tabsScrollRightButton
        );

        if (
        tabsScrollRightButton &&
        self.root.contains(
            tabsScrollRightButton
        )
        ) {
        event.preventDefault();

        self.scrollFamilyTabs(1);

        return;
        }

        var mobileFilterOpenButton =
        event.target.closest(
            self.config.mobileFilterOpenButton
        );

        if (
        mobileFilterOpenButton &&
        self.root.contains(
            mobileFilterOpenButton
        )
        ) {
        event.preventDefault();

        self.openMobileFilters();

        return;
        }


        var mobileFilterCloseButton =
        event.target.closest(
            self.config.mobileFilterCloseButton
        );

        if (
        mobileFilterCloseButton &&
        self.root.contains(
            mobileFilterCloseButton
        )
        ) {
        event.preventDefault();

        self.closeMobileFilters();

        return;
        }


        var mobileFilterOverlay =
        event.target.closest(
            self.config.mobileFilterOverlay
        );

        if (
        mobileFilterOverlay &&
        self.root.contains(
            mobileFilterOverlay
        )
        ) {
        event.preventDefault();

        self.closeMobileFilters();

        return;
        }
        
        var quantityButton =
            event.target.closest(
            "[data-quantity-selector]"
            );

        if (
            quantityButton &&
            self.root.contains(quantityButton)
        ) {
            event.preventDefault();
            event.stopPropagation();

            var quantityWrapper =
            quantityButton.closest(
                "[data-quantity-wrapper]"
            );

            var quantityInput =
            quantityWrapper
                ? quantityWrapper.querySelector(
                    "[data-quantity-value]"
                )
                : null;

            if (!quantityInput) {
            return;
            }

            var currentValue =
            parseInt(quantityInput.value, 10);

            var minimum =
            parseInt(quantityInput.min, 10);

            var maximum =
            parseInt(quantityInput.max, 10);

            if (!Number.isFinite(currentValue)) {
            currentValue = 1;
            }

            if (!Number.isFinite(minimum)) {
            minimum = 1;
            }

            if (!Number.isFinite(maximum)) {
            maximum = 9999;
            }

            var nextValue =
            quantityButton.hasAttribute(
                "data-increase-qty"
            )
                ? currentValue + 1
                : currentValue - 1;

            nextValue = Math.max(
            minimum,
            Math.min(maximum, nextValue)
            );

            quantityInput.value =
            String(nextValue);

            quantityInput.dispatchEvent(
            new Event("input", {
                bubbles: true
            })
            );

            quantityInput.dispatchEvent(
            new Event("change", {
                bubbles: true
            })
            );

            return;
        }

      var clickedSearchControl =
        event.target.closest(
            "[data-singles-search-multiselect]"
        );

        self.closeAllSearchMultiselects(
        clickedSearchControl
        );

        var clickedSearchInput =
        event.target.closest(
            "[data-singles-search-input]"
        );

        if (
        clickedSearchInput &&
        self.root.contains(
            clickedSearchInput
        )
        ) {
        self.openSearchMultiselect(
            clickedSearchInput
        );

        return;
        }


        var searchRemoveButton =
        event.target.closest(
            "[data-singles-search-remove]"
        );

        if (
        searchRemoveButton &&
        self.root.contains(
            searchRemoveButton
        )
        ) {
        event.preventDefault();

        self.toggleFilterValue(
            searchRemoveButton.getAttribute(
            "data-singles-filter-key"
            ),
            searchRemoveButton.getAttribute(
            "data-singles-filter-value"
            )
        );

        return;
        }


        var searchOption =
        event.target.closest(
            "[data-singles-search-option]"
        );

        if (
        searchOption &&
        self.root.contains(
            searchOption
        )
        ) {
        event.preventDefault();

        var searchKey =
            searchOption.getAttribute(
            "data-singles-filter-key"
            );

        self.toggleFilterValue(
            searchKey,
            searchOption.getAttribute(
            "data-singles-filter-value"
            )
        );

        return;
        } 
        
      var conditionButton = event.target.closest("[data-singles-condition]");

      if (conditionButton && self.root.contains(conditionButton)) {
        event.preventDefault();
        self.handleConditionSelection(conditionButton);
        return;
      }

      var stockButton = event.target.closest(
        self.config.stockButton
        );

        if (
        stockButton &&
        self.root.contains(stockButton)
        ) {
        event.preventDefault();

        var nextAvailability =
            stockButton.getAttribute(
            "data-singles-stock-filter"
            ) || "all";

        /*
            A single sidebar In Stock button behaves like
            an on/off toggle.

            The two-button toolbar continues behaving like
            normal All / In Stock state controls.
        */
        if (
            stockButton.hasAttribute(
            "data-singles-stock-toggle"
            ) &&
            self.state.availability === "instock"
        ) {
            nextAvailability = "all";
        }

        self.setAvailability(
            nextAvailability
        );

        return;
        }

      var viewButton =
        event.target.closest(
            "button, a, [role='button']"
        );

        if (
        viewButton &&
        self.root.contains(viewButton) &&
        viewButton.matches(
            self.config.viewButton
        )
        ) {
        event.preventDefault();

        self.setView(
            viewButton.getAttribute(
            "data-singles-view"
            )
        );

        return;
        }

      /* Card-name X button */  
      var clearNameButton =
        event.target.closest(
            self.config.nameClearButton
        );

        if (
        clearNameButton &&
        self.root.contains(
            clearNameButton
        )
        ) {
        event.preventDefault();

        /*
            Cancel any pending delayed search
            before clearing the field.
        */
        window.clearTimeout(
            self.searchTimer
        );

        var nameInput =
            self.root.querySelector(
            self.config.nameInput
            );

        if (nameInput) {
            nameInput.value = "";
        }

        self.setNameSearch("");

        if (nameInput) {
            nameInput.focus();
        }

        return;
        }  

      var clearFiltersButton = event.target.closest(self.config.clearFiltersButton);

      if (clearFiltersButton && self.root.contains(clearFiltersButton)) {
        event.preventDefault();
        self.clearFilters();
        return;
      }

      var filterButton = event.target.closest(self.config.filterButton);

      if (filterButton && self.root.contains(filterButton)) {
        event.preventDefault();
        self.toggleFilterValue(
          filterButton.getAttribute("data-singles-filter-key"),
          filterButton.getAttribute("data-singles-filter-value")
        );
      }
    });

    this.root.addEventListener(
    "focusin",
    function (event) {
        var searchInput =
        event.target.closest(
            "[data-singles-search-input]"
        );

        if (
        !searchInput ||
        !self.root.contains(
            searchInput
        )
        ) {
        return;
        }

        self.openSearchMultiselect(
        searchInput
        );
    }
    );

    if (this.tabsScroll) {
    this.tabsScroll.addEventListener(
        "scroll",
        function () {
        if (self.tabsScrollFrame) {
            window.cancelAnimationFrame(
            self.tabsScrollFrame
            );
        }

        self.tabsScrollFrame =
            window.requestAnimationFrame(
            function () {
                self.updateTabsScrollButtons();
                self.tabsScrollFrame = null;
            }
            );
        },
        {
        passive: true
        }
    );

    if (
        typeof window.ResizeObserver ===
        "function"
    ) {
        this.tabsResizeObserver =
        new ResizeObserver(
            function () {
            self.updateTabsScrollButtons();
            }
        );

        this.tabsResizeObserver.observe(
        this.tabsScroll
        );
    }

    window.requestAnimationFrame(
        function () {
        self.centerActiveFamilyTab();
        }
    );
    }

    this.root.addEventListener("change", function (event) {
      var sortControl = event.target.closest(self.config.sortControl);

      if (sortControl && self.root.contains(sortControl)) {
        event.preventDefault();
        self.setSort(sortControl.value);
      }
    });

    this.root.addEventListener(
    "input",
    function (event) {
        var searchInput =
        event.target.closest(
            "[data-singles-search-input]"
        );

        if (
        searchInput &&
        self.root.contains(
            searchInput
        )
        ) {
        self.openSearchMultiselect(
            searchInput
        );

        self.updateSearchMultiselectSuggestions(
            searchInput
        );

        return;
        }

        var nameInput =
        event.target.closest(
            self.config.nameInput
        );

        if (
        !nameInput ||
        !self.root.contains(
            nameInput
        )
        ) {
        return;
        }

        window.clearTimeout(
        self.searchTimer
        );

        self.searchTimer =
        window.setTimeout(
            function () {
            self.setNameSearch(
                nameInput.value
            );
            },
            self.config.searchDelay
        );
    }
    );

    this.root.addEventListener("search", function (event) {
      var nameInput = event.target.closest(self.config.nameInput);

      if (!nameInput || !self.root.contains(nameInput)) {
        return;
      }

      window.clearTimeout(self.searchTimer);
      self.setNameSearch(nameInput.value);
    });

    document.addEventListener(
    "keydown",
    function (event) {
        if (event.key === "Escape") {
        self.closeMobileFilters();
        }
    }
    );

    window.addEventListener(
    "resize",
    function () {
        if (
        window.innerWidth >
        Number(
            self.config.mobileFilterBreakpoint ||
            900
        )
        ) {
        self.closeMobileFilters();
        }
        self.updateTabsScrollButtons();
        
    }
    );
  };

  SinglesGridInstance.prototype.loadEmbeddedRows =
  function (options) {
    options = options || {};

    var rowsElementId =
      normalizeText(
        options.rowsElementId ||
        this.config.rowsElementId
      );

    if (!rowsElementId) {
      return Promise.reject(
        new Error(
          "The embedded singles rows element ID is missing."
        )
      );
    }

    var rowsElement =
      document.getElementById(
        rowsElementId
      );

    if (!rowsElement) {
      this.setLoading(false);

      this.showError(
        "The embedded singles search data could not be found."
      );

      return Promise.reject(
        new Error(
          "Embedded singles rows element was not found: " +
          rowsElementId
        )
      );
    }

    this.setLoading(true);

    renderSkeletonIntoGrid(
      this.grid,
      this.state.view
    );

    try {
      var rows =
        parseSinglesRows(
          rowsElement.textContent ||
          "[]",
          "#" + rowsElementId
        );

      this.setRows(rows);
      this.setLoading(false);

      dispatch(
        "singles:grid:embeddedrows",
        {
          game:
            this.config.game,

          rows:
            rows,

          instance:
            this
        }
      );

      return Promise.resolve(
        rows
      );

    } catch (error) {
      this.setLoading(false);

      this.showError(
        error &&
        error.message
          ? error.message
          : "The singles search results could not be loaded."
      );

      return Promise.reject(
        error
      );
    }
  };

  SinglesGridInstance.prototype.load = function (options) {
    options = options || {};

    var jsonUrl = normalizeText(
      options.jsonUrl || this.config.jsonUrl
    );

    if (!jsonUrl) {
      return Promise.reject(new Error("The collection JSON URL is missing."));
    }

    this.setLoading(true);

    renderSkeletonIntoGrid(
    this.grid,
    this.state.view
    );

    var self = this;

    return loadCompleteCollection(jsonUrl, {
    productCount:
        options.productCount !== undefined
        ? options.productCount
        : this.config.productCount,

    pageSize:
        options.pageSize ||
        this.config.pageSize,

    maximumPages:
        options.maximumPages ||
        this.config.maximumPages,

    maxConcurrentRequests:
        options.maxConcurrentRequests !== undefined
        ? options.maxConcurrentRequests
        : this.config.maxConcurrentRequests,

    requestRetries:
        options.requestRetries !== undefined
        ? options.requestRetries
        : this.config.requestRetries,

    retryDelay:
        options.retryDelay !== undefined
        ? options.retryDelay
        : this.config.retryDelay,

    requestTimeout:
        options.requestTimeout !== undefined
        ? options.requestTimeout
        : this.config.requestTimeout,

    serverSort:
        options.serverSort ||
        this.config.serverSort ||
        "",

    forceReload:
        options.forceReload === true
    }).then(function (rows) {
      self.setRows(rows);
      self.setLoading(false);

      return rows;
    }).catch(function (error) {
      self.setLoading(false);
      self.showError(error && error.message ? error.message : "An unknown loading error occurred.");

      throw error;
    });
  };

  SinglesGridInstance.prototype.initialize = function () {
    if (!this.resolveElements()) {
      console.warn("Singles grid container was not found for", this.config.game);
      return Promise.resolve(this);
    }

    this.bindEvents();
    this.syncControls();

    var self = this;

    /*
      Search pages already contain their rows
      in embedded JSON.

      The shared grid owns reading and
      installing those rows.
    */
    if (
      normalizeText(
        this.config.rowsElementId
      )
    ) {
      return this
        .loadEmbeddedRows()
        .then(function () {
          return self;
        });
    }

    /*
      Some collection adapters deliberately
      manage their own collection loading.
    */
    if (
      this.config.autoLoad === false
    ) {
      return Promise.resolve(
        this
      );
    }

    /*
      Normal collection JSON loading.
    */
    return this
      .load()
      .then(function () {
        return self;
      });
    };

  function initializeMobileImagePreview() {
    if (
      document.documentElement.dataset
        .mobilePreviewInitialized === "true"
    ) {
      return;
    }

    document.documentElement.dataset
      .mobilePreviewInitialized = "true";

    var mobileQuery =
      window.matchMedia(
        "(max-width: 900px)"
      );

    var lastPreviewTrigger = null;

    function getModal() {
      return document.querySelector(
        "[data-mobile-preview-modal]"
      );
    }

    function openPreview(
      previewTrigger
    ) {
      var modal = getModal();

      if (!modal) {
        return;
      }

      var modalImage =
        modal.querySelector(
          "[data-mobile-preview-image]"
        );

      if (!modalImage) {
        return;
      }

      var largeImage =
        previewTrigger.querySelector(
          ".singles-list-image-preview img"
        );

      var thumbnail =
        previewTrigger.querySelector(
          ".singles-list-image-thumb"
        );

      var sourceImage =
        largeImage ||
        thumbnail;

      if (!sourceImage) {
        return;
      }

      var imageSource =
        sourceImage.currentSrc ||
        sourceImage.getAttribute(
          "src"
        ) ||
        "";

      if (!imageSource) {
        return;
      }

      lastPreviewTrigger =
        previewTrigger;

      var previewCard =
        previewTrigger.closest(
          "[data-singles-list-card]"
        );

      modal.classList.toggle(
        "is-foil",
        Boolean(
          previewCard &&
          previewCard.classList.contains(
            "is-foil"
          )
        )
      );

      modalImage.src =
        imageSource;

      modalImage.alt =
        sourceImage.alt ||
        (
          thumbnail
            ? thumbnail.alt
            : ""
        ) ||
        "Enlarged card image";

      modal.hidden = false;

      modal.setAttribute(
        "aria-hidden",
        "false"
      );

      document.body.classList.add(
        "singles-mobile-image-open"
      );

      var closeButton =
        modal.querySelector(
          "[data-mobile-preview-close]"
        );

      if (closeButton) {
        window.setTimeout(
          function() {
            closeButton.focus();
          },
          0
        );
      }
    }

    function closePreview() {
      var modal = getModal();

      if (
        !modal ||
        modal.hidden
      ) {
        return;
      }

      var modalImage =
        modal.querySelector(
          "[data-mobile-preview-image]"
        );

      modal.hidden = true;

      modal.classList.remove(
        "is-foil"
      );

      modal.setAttribute(
        "aria-hidden",
        "true"
      );

      document.body.classList.remove(
        "singles-mobile-image-open"
      );

      if (modalImage) {
        modalImage.removeAttribute(
          "src"
        );

        modalImage.alt = "";
      }

      if (
        lastPreviewTrigger &&
        document.contains(
          lastPreviewTrigger
        )
      ) {
        lastPreviewTrigger.focus();
      }

      lastPreviewTrigger = null;
    }

    document.addEventListener(
      "click",
      function(event) {
        var previewTrigger =
          event.target.closest(
            "[data-singles-mobile-preview]"
          );

        if (
          previewTrigger &&
          mobileQuery.matches &&
          previewTrigger.closest(
            "[data-singles-grid].is-list-view"
          )
        ) {
          event.preventDefault();
          event.stopPropagation();

          openPreview(
            previewTrigger
          );

          return;
        }

        var closeButton =
          event.target.closest(
            "[data-mobile-preview-close]"
          );

        if (closeButton) {
          event.preventDefault();

          closePreview();

          return;
        }

        var modal =
          event.target.closest(
            "[data-mobile-preview-modal]"
          );

        if (
          modal &&
          event.target === modal
        ) {
          closePreview();
        }
      }
    );

    document.addEventListener(
      "keydown",
      function(event) {
        if (
          event.key === "Escape"
        ) {
          closePreview();
        }
      }
    );
  }

  /* ======================================================
    Shared finish detection
  ====================================================== */

  function normalizeFinishMatchText(
    value
  ) {
    return normalizeLower(value)
      .replace(/[-–—]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function resolveFinishBadge(
    details,
    finish,
    config
  ) {
    config = config || {};

    var normalizedDetails =
      normalizeFinishMatchText(
        details
      );

    var normalizedFinish =
      normalizeFinishMatchText(
        finish
      );

    var combinedText =
      [
        normalizedDetails,
        normalizedFinish
      ]
        .filter(Boolean)
        .join(" ");

    /*
      Nonfoil terms win immediately.

      This prevents something such as
      "Nonfoil" from accidentally matching
      the generic "foil" term.
    */
    var nonfoilTerms =
      toArray(
        config.nonfoilTerms
      )
        .map(
          normalizeFinishMatchText
        )
        .filter(Boolean);

    var isNonfoil =
      nonfoilTerms.some(
        function(term) {
          return (
            normalizedFinish.indexOf(
              term
            ) !== -1 ||
            normalizedDetails.indexOf(
              term
            ) !== -1
          );
        }
      );

    if (isNonfoil) {
      return null;
    }

    /*
      Check special finishes before the
      game's generic foil term.

      Longest matches are checked first so
      "Reverse Holofoil" wins over "Holofoil",
      "Galaxy Foil" wins over "Foil", etc.
    */
    var specialFinishes =
      toArray(
        config.specialFinishes
      )
        .slice()
        .sort(
          function(a, b) {
            return (
              normalizeFinishMatchText(
                b && b.match
              ).length -
              normalizeFinishMatchText(
                a && a.match
              ).length
            );
          }
        );

    for (
      var index = 0;
      index < specialFinishes.length;
      index += 1
    ) {
      var definition =
        specialFinishes[index] || {};

      var match =
        normalizeFinishMatchText(
          definition.match
        );

      if (
        match &&
        combinedText.indexOf(
          match
        ) !== -1
      ) {
        return {
          text:
            normalizeText(
              definition.text ||
              definition.label ||
              definition.match
            ),

          cls:
            normalizeText(
              definition.cls ||
              definition.className ||
              "badge-special"
            ),

          removeText:
            normalizeText(
              definition.removeText ||
              definition.match
            )
        };
      }
    }

    /*
      Finally check the game's generic
      foil vocabulary.
    */
    var genericFoilTerms =
      toArray(
        config.genericFoilTerms
      )
        .map(
          normalizeFinishMatchText
        )
        .filter(Boolean);

    var genericMatch =
      genericFoilTerms.some(
        function(term) {
          return (
            normalizedFinish.indexOf(
              term
            ) !== -1 ||
            normalizedDetails.indexOf(
              term
            ) !== -1
          );
        }
      );

    if (!genericMatch) {
      return null;
    }

    return {
      text:
        normalizeText(
          config.genericText
        ) ||
        "FOIL",

      cls:
        normalizeText(
          config.genericClass
        ) ||
        "badge-regular",

      removeText:
        normalizeText(
          config.genericRemoveText
        ) ||
        normalizeText(
          genericFoilTerms[0]
        ) ||
        "Foil"
    };
  }

  function parseCardTitleDetails(
    title,
    finish,
    finishConfig
  ) {
    title =
      String(title || "");

    /*
      Preserve the existing behavior that removes
      standalone numeric parenthetical additions.
    */
    title =
      title.replace(
        /\s*\(\d+\)\s*/g,
        " "
      );

    title =
      title.replace(
        /\s+/g,
        " "
      ).trim();

    var match =
      title.match(
        /^(.*?)\s*\((.*?)\)\s*$/
      );

    var name =
      title;

    var rawDetails =
      "";

    if (match) {
      name =
        match[1].trim();

      rawDetails =
        match[2].trim();
    }

    var badge =
      resolveFinishBadge(
        rawDetails,
        finish,
        finishConfig || {}
      );

    var parts =
      rawDetails
        .split(",")
        .map(function(part) {
          return part.trim();
        })
        .filter(Boolean);

    if (badge) {
      parts =
        parts.filter(
          function(part) {
            return (
              part.toLowerCase() !==
              String(
                badge.removeText || ""
              ).toLowerCase()
            );
          }
        );
    }

    return {
      name: name,

      details:
        parts.join(" · "),

      rawDetails:
        rawDetails,

      badge:
        badge
    };
  }

  function renderFinishBadge(
    badge,
    options
  ) {
    options = options || {};

    if (!badge) {
      return "";
    }

    var mode =
      options.mode === "list"
        ? "list"
        : "grid";

    var classes = [];

    if (mode === "list") {
      classes.push(
        "singles-grid-foil-badge",
        "singles-list-finish-badge"
      );
    } else {
      classes.push(
        "singles-grid-foil-badge",
        "singles-grid-finish-badge"
      );
    }

    if (options.className) {
      classes.push(
        normalizeText(
          options.className
        )
      );
    }

    if (badge.cls) {
      classes.push(
        normalizeText(
          badge.cls
        )
      );
    }

    return (
      "<span" +
        " class=\"" +
          escapeAttribute(
            classes.join(" ")
          ) +
        "\"" +
      ">" +
        escapeHtml(
          badge.text || ""
        ) +
      "</span>"
    );
  }


  function renderSetContent(
    setName,
    options
  ) {
    options = options || {};

    var html =
      options.iconHtml || "";

    html +=
      "<span>" +
        escapeHtml(
          setName || ""
        ) +
      "</span>";

    return html;
  }


  function renderGridSet(
    setName,
    options
  ) {
    options = options || {};

    return (
      "<div" +
        " class=\"singles-grid-set\"" +
      ">" +
        renderSetContent(
          setName,
          options
        ) +
      "</div>"
    );
  }

  function renderGridTitleDetails(
    value
  ) {
    var text =
      normalizeText(value);

    return (
      "<div" +
        " class=\"singles-grid-title-extra\"" +
      ">" +
        (
          text
            ? (
                "<span" +
                  " class=\"singles-grid-title-details\"" +
                ">" +
                  escapeHtml(text) +
                "</span>"
              )
            : ""
        ) +
      "</div>"
    );
  }

  /* ======================================================
     Public API
  ====================================================== */

  function create(config) {
    return new SinglesGridInstance(config);
  }

  window.SinglesGrid = {
    version: VERSION,

    create: create,

    finish: {
      parseTitle:
        parseCardTitleDetails
    },

    renderSkeleton:
      renderSkeletonIntoGrid,

    createCollectionNavigation:
      createCollectionNavigation,

    render: {
      gridConditions:
        renderGridConditions,

      gridCard:
        renderGridCard,

      listCard:
        renderListCard,

      updateGridCardVariant:
        updateGridCardVariant,

      updateListCardVariant:
        updateListCardVariant,

      finishBadge:
        renderFinishBadge,

      setContent:
        renderSetContent,

      gridSet:
        renderGridSet,

      gridTitleDetails:
        renderGridTitleDetails
    },

    helpers: {
      escapeHtml:
        escapeHtml,

      escapeAttribute:
        escapeAttribute,

      isAvailable:
        isAvailable,

      getQuantity:
        getQuantity,

      cardHasStock:
        cardHasStock
    }
  };

  initializeMobileImagePreview();

  bindNotifyButtons();
  bindKlaviyoNotifyForm();
  dispatch("singles:grid:ready", {
    version: VERSION,
    api: window.SinglesGrid
  });
})(window, document);

